import type { EquipmentNature } from './equipment-types';
import type { AppDeviceNature, AppState } from '@orgazone/applications';
// Client de l'API /v1 de la Console (même origine ; cookies de session inclus). Calqué sur apps/store/web.
import type { AuthMode } from '@orgazone/auth-web/client';
import type { VocabularyLang } from '@orgazone/sync-contract';
import type { TakeoverItem, TakeoverPreview, TakeoverReport } from '@orgazone/sync-contract';
import { bounceOnUnauthorized } from '@orgazone/ui-web';

export type { AuthMode };

export interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
  status: 'invited' | 'active' | 'disabled';
  /** Permissions EFFECTIVES calculées par le serveur (même matrice que les gardes de routes) — cf. `can()`. */
  permissions?: string[];
}
/** Ligne d'annuaire — tenant (client) + nb de magasins + métier (pour la liste Clients). */
/** Une caisse vue par la SUPERVISION : son dernier pull reel, et jusqu'ou elle a lu. */
export interface FleetRegister { registerId: string; appVersion: string | null; lastSeenAt: number; channels: number }
/** Un terminal de stock (Mobile Partner) — listé à part des caisses. */
export interface FleetTerminal { deviceId: string; equipmentCode: string | null; family: string | null; appVersion: string | null; lastSeenAt: number; channels: number }
/** Un magasin supervise.  renseigne = sa base n'a pas repondu ;  false =
 *  declare au BO mais absent de l'annuaire, un ecart qui doit se voir. */
/** Un objet qui survit sans magasin pour le réclamer — `detail` dit CE QUE ça coûte de le laisser. */
export interface Orphelin { gln: string; nom: string | null; detail?: string }
export interface RapportOrphelins {
  magasinsConnus: number;
  registre: Orphelin[];
  sitesEdi: Orphelin[];
  bases: Orphelin[];
  roles: Orphelin[];
  /** ⚠️ Les sources qu'on n'a PAS pu interroger : sans elles, une liste vide serait ambiguë. */
  nonVerifie: string[];
}

export interface SupervisionStore {
  gln: string; name: string; sCode: string | null; status: string | null; tenantName: string | null;
  /** L'annuaire de la Console : `null` pour un magasin connu du back-office seul (« hors annuaire »). */
  storeId: string | null; tenantId: string | null; kind: 'store' | 'depot' | null;
  knownInDirectory: boolean; registers: FleetRegister[]; terminals: FleetTerminal[];
  /** Appareils affectés au magasin et jamais mis en service : aucun contact à juger, mais comptés. */
  unpaired: number;
  /** La version attendue de la caisse — celle du CANAL du magasin ; `null` : non jugée, `versionSource` dit pourquoi. */
  expectedVersion: string | null; versionSource: string;
  /** Le contact le PLUS RÉCENT — ⚠️ l'état se juge sur le plus ANCIEN (`storeHealth`), jamais sur celui-ci. */
  lastSeenAt: string | null; error: string | null;
}

export interface TenantRow {
  id: string;
  name: string;
  siren: string | null;
  businessKind: string | null;
  topology: 'mono' | 'multi';
  origin: 'wizard' | 'web';
  status: string;
  createdAt: string;
  /** Createur en « Prenom Nom », resolu a la lecture ; repli sur l e-mail si le compte n existe plus.
   *  null pour les clients anterieurs au 2026-08-13 (la colonne n existait pas). */
  createdBy: string | null;
  storeCount: number;
  /** Blocages EN COURS du client, tous périmètres confondus (abonnement, magasin, appareil) — la colonne « État ». */
  blocks: TenantBlock[];
}

/** Un blocage en cours d'un client, avec ce qu'il faut pour dire où il porte (« R01 · Sélestat »). */
export interface TenantBlock {
  scope: 'subscription' | 'store' | 'device';
  level: 'warn' | 'lock';
  /** Le magasin visé, ou celui de l'appareil visé — `null` pour un abonnement. */
  storeName: string | null;
  /** Le numéro de la caisse visée — `null` pour un autre appareil. */
  registerNumber: number | null;
  /** Le code d'un appareil qui n'est pas une caisse (« MP01 »). */
  equipmentCode: string | null;
}

/** Fiche client complète (onglet Identité). */
export interface TenantFull {
  id: string; name: string; commercialName: string | null; siren: string | null; siretHq: string | null;
  naf: string | null; vatIntra: string | null; legalForm: string | null;
  /** Régime de TVA : `true` franchise en base (art. 293 B), `false` assujetti, `null` pas encore réglé à la Console. */
  vatFranchise: boolean | null;
  address: { voie?: string; cp?: string; ville?: string } | null;
  contact: { nom?: string; email?: string; tel?: string } | null;
  businessKindDefault: string | null; topology: 'mono' | 'multi'; origin: 'wizard' | 'web';
  /** Agrégat multi-magasin créé ? (le DSN lui-même ne quitte JAMAIS le serveur — routage only). */
  hasAggregate: boolean; status: string; createdAt: string;
}
export interface TenantStore { id: string; name: string; kind: 'store' | 'depot'; gln: string; sCode: string | null; radicalEan: string | null; businessKind: string | null; status: string; syncMode: 'mono' | 'bi'; siret: string | null; address: { voie?: string; cp?: string; ville?: string } | null; boDeclaredAt: string | null;
  /** Dernière déclaration RÉUSSIE à l'EDI — null : aucune enregistrée (dont un magasin déclaré avant que la Console garde la date). */
  ediDeclaredAt: string | null;
  /** Devise du magasin, figée à la création (code ISO 4217). */
  currency: string;
  /** Compteur REEL de numerotation des caisses (le prochain numero sera celui-ci + 1). */
  registerCounter: number | null;
  /** OU vit la base de ce magasin : `hostname:port`, extrait du DSN cote serveur. ⚠️ Jamais le DSN
   *  lui-meme (il porte le mot de passe du role magasin). null = pas encore de base, ou DSN illisible. */
  dbHost: string | null;
  /** Création du magasin — dit si un journal de reprises vide prouve une absence de reprise. */
  createdAt: string | null }
export interface TenantDevice { id: string; family: string;
  /** Le nom de l'appareil au parc (« Chalet 5 caisse 1 ») — `null` : un appareil entré avant lui, dit « Sans nom ». */
  name: string | null;
  /** Le code du type d'équipement (référentiel), et sa NATURE — ce qui range l'appareil (`equipment-types.ts`). */
  equipmentType: string | null; nature: EquipmentNature | null;
  model: string | null; serial: string | null; status: string; storeId: string | null; rentalType: string | null; rentalEnd: string | null; appVersion: string | null; lastSeenAt: string | null; registerNumber: number | null; storeCode: string | null;
  /** Echeance du jeton d appairage ENCORE VALIDE, s il en existe un. null = aucun QR a montrer. */
  pairingExpiresAt: string | null }
/** Ticket d'entrée cross-tenant : où le poster, et quoi poster. Valable une fois, quelques minutes. */
export interface CrossTenantEntry { ok: true; entryUrl: string; ticket: string; storeName: string; expiresAt: string }
export interface TenantLicense { id: string; product: string; status: string; startsAt: string | null; endsAt: string | null }
/**
 * Le droit d'envoyer d'un magasin sur UN canal (cadrage §11.8).
 * ⚠️ `allowance: null` = autorisé · `0` = bloqué. Un plafond chiffré n'est pas encore appliqué : sa
 * fenêtre de temps reste à trancher, et l'offrir à l'écran serait promettre une règle inexistante.
 */
export interface BoChannelAllowance {
  channel: 'email' | 'sms' | 'wallet_push';
  allowance: number | null;
  reason: string | null;
  updatedAt: number | null;
  updatedBy: string | null;
}
/** Un compte d'envoi enregistré, et les magasins qui s'en servent (un compte PARTAGÉ en a plusieurs). */
export interface BoMessagingAccount {
  id: string; provider: string; externalId: string; login: string; label: string;
  /**
   * ⚠️ « un mot de passe est détenu », PAS « il peut envoyer » — ce dernier ne s'obtient qu'en
   * éprouvant le compte (`BoStoreAccountProbe.canSend`). Un mot de passe changé chez l'opérateur
   * laisse celui-ci vrai pendant que l'envoi échoue.
   */
  hasSecret: boolean;
  stores: Array<{ gln: string; name: string | null; attachedAt: string | null }>;
  createdAt: string | null; updatedAt: string | null;
}
/** L'épreuve du compte d'un magasin — DEUX faits distincts, qui ne se réparent pas au même endroit. */
export interface BoStoreAccountProbe {
  ok: boolean; error?: string;
  account?: { id: string; externalId: string; login: string };
  /** Les crédits que l'opérateur reconnaît au sous-compte, par type de message. */
  credits?: Array<{ type: string; quantity: number }>;
  creditsError?: string;
  /** Savons-nous nous authentifier comme ce compte ? C'est LUI que l'opérateur débitera. */
  canSend?: boolean;
  sendError?: string;
}

/** L'identité d'un client telle que le formulaire de modification la lit et l'envoie — `null` pour un champ vide. */
export interface TenantIdentity {
  name: string | null; commercialName: string | null; siren: string | null; siretHq: string | null; naf: string | null;
  street: string | null; zip: string | null; city: string | null; email: string | null;
  /** Régime de TVA : `true` franchise en base, `false` assujetti, `null` pas encore réglé à la Console. */
  vatFranchise: boolean | null;
  /** En franchise : un numéro de TVA lui a-t-il été attribué (opérations dans l'Union) ? */
  vatNumberAssigned: boolean;
}
/** Ce qu'un magasin a reçu de l'identité modifiée de son client. */
export interface TenantStoreResend { id: string; name: string; outcome: 'sent' | 'skipped' | 'failed'; detail: string | null }
export interface TenantDetail { tenant: TenantFull; stores: TenantStore[]; devices: TenantDevice[]; licenses: TenantLicense[] }
/** Brouillon de mise en service déposé (formulaire externe). `sireneGap` = écart déposé↔Sirene calculé
 *  côté SERVEUR au dépôt (jamais recalculé par le SPA) ; null = pas d'écart relevé ou contrôle non joué. */
/** Valeur d'une liste maître (SA-10.1). `locked` = motif fiscal verrouillé par trigger (0011). */
/** Type de commerce (corps de métier) : assemblage de listes injecté aux NOUVELLES bases. */
export interface TradeTypeRow { id: string; code: string; label: string; description: string; ord: number; lists: string[] }
export interface ReferentialRow { id: string; list: string; code: string; label: string; ord: number; locked: boolean; attrs: Record<string, unknown> }
/** Une colonne MÉTIER d'une liste : ce que la liste porte en plus du code et du libellé. */
export interface RefColDef { key: string; label: string; kind: 'text' | 'values' | 'multiline' | 'choice'; options?: { value: string; label: string }[] }
/**
 * La DÉFINITION d'une liste — ce qui la décrit, par opposition à `ReferentialRow` qui est une valeur
 * DEDANS. ⚠️ `destination` et `descend` viennent du SERVEUR : lui seul connaît le routage, et l'écran
 * qui le redéduirait finirait par annoncer autre chose que ce qui se passe.
 */
export interface RefListDef {
  key: string; label: string; addLabel: string; note: string; category: string;
  cols: RefColDef[]; orderable: boolean; storeKind: string | null; builtin: boolean; ord: number;
  descend: boolean; destination: string;
}
export interface DraftRow { id: string; identity: unknown; siren: string | null; siret: string | null; sireneGap: unknown; draftType: 'client' | 'magasin'; status: 'deposited' | 'to_correct' | 'validated'; submittedAt: string }
/** Résultat de l'annuaire des entreprises (Sirene) — autocomplétion du wizard. */
export interface SireneResult { siren: string | null; name: string; siret: string | null; naf: string | null; legalForm: string | null; street: string | null; cp: string | null; city: string | null; closed: boolean }
/** Ligne d'annuaire — magasin (rattaché à un client). */
export interface StoreRow {
  id: string;
  tenantId: string;
  tenantName: string | null;
  name: string;
  kind: 'store' | 'depot';
  gln: string;
  sCode: string | null;
  radicalEan: string | null;
  businessKind: string | null;
  status: string;
}
/** Ligne d'annuaire — appareil (flotte). ⚠️ appVersion/lastSeenAt VIENNENT de l'appareil (null si jamais vu). */
export interface DeviceRow {
  id: string;
  /** Le nom de l'appareil au parc (« Chalet 5 caisse 1 ») — `null` : un appareil entré avant lui, dit « Sans nom ». */
  name: string | null;
  /** Le LIBELLÉ de son type d'équipement. */
  family: string;
  /** Le code de son type d'équipement (référentiel « Types d'équipement ») — `null` : un appareil sans type, qui ne déclenche rien. */
  equipmentType: string | null;
  /** La NATURE de son type : c'est elle qui range l'appareil (caisse, Mobile Partner…), jamais son libellé. */
  nature: EquipmentNature | null;
  model: string | null;
  serial: string | null;
  status: 'available' | 'assigned' | 'returned' | 'revoked';
  storeId: string | null;
  tenantId: string | null;
  storeName: string | null;
  tenantName: string | null;
  rentalType: 'courte' | 'longue' | 'achat' | null;
  /** N° de CAISSE attribué à l'affectation (chaîne NF525 par registre) — null : non-caisse ou au pool. */
  registerNumber: number | null;
  /**
   * L'IDENTITÉ MAGASIN — « MP01 », attribuée à l'affectation, affichée partout où l'appareil est cité.
   * ⚠️ C'EST UN NOM, PAS DU MATÉRIEL : il désigne le rôle de la machine DANS CE MAGASIN, et c'est lui
   * que portent les documents remontés (une zone d'inventaire, demain une réception).
   * ⚠️ `null` pour une CAISSE (elle porte un n° de registre, qui est une clé fiscale) et pour un
   * appareil au pool. Jamais réutilisé dans un magasin, même après retour (SPEC tronc §1).
   */
  storeCode: string | null;
  rentalStart: string | null;
  rentalEnd: string | null;
  appVersion: string | null;
  lastSeenAt: string | null;
}

/** Jeton de mise en service ACTIF (à encoder dans le QR). L'appareil POST son n° de série sur `url` pour tirer sa config. */
export interface DevicePairingActive { token: string; url: string; expiresAt: string }
/** État d'appairage d'un appareil : jeton actif (ou null) + dernière mise en service RÉELLE (null tant qu'aucune). */
export interface DevicePairingState {
  active: DevicePairingActive | null;
  redeemedAt: string | null;
  redeemedSerial: string | null;
  /** Version declaree au DERNIER battement (source de verite). null = la caisse n a pas encore synchronise. */
  installedVersion: string | null;
  /** Version declaree au SCAN — sert a dire « relevee a l appairage » tant qu il n y a pas eu de battement. */
  declaredVersion: string | null;
  /** Version publiee au store prive. null = inconnue → aucun verdict de mise a jour n est rendu. */
  publishedVersion: string | null;
  lastBeatAt: string | null;
  registerNumber: number | null;
  /** Serie du PARC (saisie a l entree en stock) — reference du controle anti-substitution. */
  parcSerial: string | null;
  /** Blocage `lock` en cours sur l appareil, son magasin ou son client. Un appareil bloque S APPAIRE
   *  quand meme (le blocage porte sur l USAGE), mais l ecran doit le rappeler. */
  blocked: { scope: string; reason: string } | null;
}

/**
 * Rapport de MISE EN SERVICE d une base magasin — ce que le serveur a REELLEMENT fait.
 * ⚠️ Les declarations BO et EDI sont « best-effort » cote serveur (elles n annulent pas la creation),
 * d ou des champs d ERREUR : sans eux, un magasin cree mais jamais annonce au back-office semblerait
 * pret alors qu aucune caisse ne pourra synchroniser.
 */
export interface ProvisionReport {
  ok: true;
  dbName: string;
  /** Version de DDL appliquee a cette base neuve. */
  schemaVersion?: number;
  boDeclared?: boolean; boError?: string;
  ediDeclared?: boolean; ediError?: string;
  /** Lignes de referentiels REELLEMENT ecrites ; `refsNote` dit pourquoi le metier n a pas ete resolu. */
  seededRefs?: number; refsNote?: string;
  /** Identite legale : `identityError` = non descendue, `identityMissing` = descendue incomplete (champs manquants). */
  identityMissing?: string[]; identityError?: string;
  /** Compte d administration editeur descendu vers la caisse — sans lui, personne n ouvre ses reglages. */
  adminAccountPushed?: boolean; adminAccountError?: string;
  /** Amorcage du journal de synchronisation. */
  seeded?: number; seedError?: string;
  /** Vocabulaire du metier descendu. */
  vocabularyPushed?: number; vocabularyError?: string;
  /** Groupe de mise a jour des caisses NON descendu (absent = descendu, ou flux public : rien a descendre). */
  updateGroupError?: string;
}

/** Un groupe tel que le store le publie : `servedVersion: null` = il suit le flux public de l'application. */
export interface StoreGroup { name: string; enrollToken: string; servedVersion: string | null }

/** Une application telle que le store de cet environnement la décrit (miroir de `server/src/store-apps.ts`). */
export interface StoreAppEntry { slug: string; name: string; isPublished: boolean; publicVersion: string | null; groups: StoreGroup[] }
/** Ce que le store dit de l'une de nos applications — `unknown` : le store n'a pas pu être lu, rien n'est affirmé. */
export type AppStoreStatus =
  | { kind: 'present'; entry: StoreAppEntry }
  | { kind: 'absent'; lookalike: StoreAppEntry | null }
  | { kind: 'unknown'; error: string };
/** L'une de nos applications (`@orgazone/applications`), rapprochée du store par son nom exact. */
export interface StoreAppLine {
  slug: string; name: string; description: string; state: AppState; nature: AppDeviceNature | null;
  installable: boolean; store: AppStoreStatus;
}
/** Le relevé du store rapproché de nos applications ; `orphans` = ce que le store porte et qui n'est aucune des nôtres. */
export interface StoreAppsSurvey { storeUrl: string | null; lines: StoreAppLine[]; orphans: StoreAppEntry[]; error: string | null }
/** Le groupe choisi pour une application d'un magasin — aucune ligne = flux public. */
export interface UpdateGroupChoice { app: string; enrollToken: string; groupName: string; updatedAt: string; updatedBy: string | null }

/** Reglage de messagerie sortante. ⚠️ Le SECRET n est jamais renvoye : seulement s il est defini.
 *  `effective` dit ce qui s applique VRAIMENT (base / env / aucun), pas ce qui est enregistre. */
export interface MailSettings {
  transport: 'smtp' | 'mailjet' | 'none';
  fromEmail: string | null; fromName: string | null;
  smtpHost: string | null; smtpPort: number | null; smtpSecure: boolean; smtpUser: string | null;
  apiKey: string | null; secretSetAt: string | null;
  updatedAt: string | null; updatedBy: string | null;
  effective: 'base' | 'env' | 'aucun'; note?: string;
}

/** Un reglage de plateforme du back-office. ⚠️ `value` est PUBLIQUE, le secret ne redescend jamais :
 *  seule sa date de pose voyage, pour qu on sache s il est defini sans jamais le relire. */
export interface BoPlatformSetting {
  key: string; note: string; value: string | null;
  hasSecret: boolean; secretSetAt: string | null;
  /** Valeurs admises quand elles sont finies — l ecran en fait un choix, pas un champ libre. */
  choices?: readonly { readonly value: string; readonly label: string }[];
  /** Ce qui manque pour que le reglage AGISSE — absent = il est consomme. */
  notWired?: string;
  updatedAt: string | null; updatedBy: string | null;
}

/** L etat des reglages du back-office, en une lecture : sa plateforme ET sa messagerie. */
export interface BoSettingsState { platform: BoPlatformSetting[]; mail: MailSettings }


/** L'etat du lanceur du transfert wallet. `demarre: false` = il ne tourne PAS (ce n'est pas "sain"). */
export interface WalletRunner {
  started: boolean;
  cadenceSec: number;
  cadenceSource: 'réglage' | 'défaut';
  lastRunAt: number | null;
  durationMs: number | null;
  lastRun: { gln: string; sent: number; deferred: number; dead: number; blocked?: string }[];
  lastError: string | null;
  runningTotals: { cycles: number; sent: number; dead: number };
}

/** La file d un magasin. `plusAncienAt` trahit un arret : une file vide, seule, ne prouve rien. */
export interface WalletFile {
  gln: string;
  pending: number;
  oldestAt: number | null;
  dead: { clientId: string; error: string; attempts: number }[];
}

/** L etat complet du transfert wallet, tel que le back-office le rend. */
export interface WalletState {
  runner: WalletRunner;
  stores: WalletFile[];
  unreadable: { gln: string; error: string }[];
  total: { pending: number; dead: number };
  tokens: { gln: string; token: 'absent' | 'illisible' | 'ok' }[];
}

/** Lien d invitation emis : l URL part TOUJOURS (copiable), l e-mail dit son issue reelle. */
export interface DraftInviteResult { url: string; expiresAt: string; mail: { ok: boolean; error?: string } }

/** Un ecart de propagation, a la cle STABLE — c est elle qu on renvoie pour IMPOSER un conflit. */
export interface RefDiffItem {
  key: string; listKey: string; table: string; code: string;
  masterLabel: string; storeLabel?: string; masterDetail?: string; storeDetail?: string;
}
/** Ecart maitre <-> magasin (analyze). `skipped`/`error` = magasin non analysable, avec la raison REELLE. */
export interface PropagationStoreDiff {
  id: string; name: string; gln: string;
  skipped?: string; error?: string;
  boDeclared?: boolean; tradeCode?: string | null; note?: string;
  adds?: RefDiffItem[]; conflicts?: RefDiffItem[]; unchanged?: number; extras?: string[];
}
/** Resultat d application par magasin — la journalisation BO dit son etat REEL (jamais presumee). */
export interface PropagationApplyResult {
  id: string; name?: string; error?: string;
  added?: number; imposed?: number;
  journal?: { ok: boolean; note: string };
}

// ── Vocabulaire (CADRAGE-Referentiels-Vocabulaire §6ter) — le mot d'un champ, par métier et par langue ──
/** Les langues du vocabulaire, celles du contrat (`VOCABULARY_LANGS`) — jamais une liste recopiée ici. */
export type { VocabularyLang };
export type VocabularyTexts = Record<VocabularyLang, string>;
/** Un champ d'article qui a un mot — `platform` est le filet de la plateforme, jamais vide. */
export interface VocabularyField { key: string; column: string; platform: VocabularyTexts }
/** Un terme du dictionnaire. `builtin` = la graine issue du catalogue : elle ne se supprime pas. */
export interface VocabularyTerm { key: string; builtin: boolean; texts: VocabularyTexts }
/** Un type de commerce et ses DÉROGATIONS seulement : un champ absent de `fields` suit le terme de plateforme. */
export interface VocabularyTrade { code: string; label: string; fields: Record<string, string> }
export interface VocabularyState { fields: VocabularyField[]; terms: VocabularyTerm[]; trades: VocabularyTrade[] }
/** Une ligne `vocab_default.<champ>.<langue>` qui DIFFÈRE dans la base du magasin ; `from` null = absente. */
export interface VocabularyChange { name: string; from: string | null; to: string }
/** L'analyse d'un magasin — `skipped`/`error` = non analysable, avec la raison RÉELLE ; `changes` vide = à jour. */
export interface VocabularyPropagationStore {
  id: string; name: string; gln: string; trade: string | null;
  note?: string; skipped?: string; error?: string;
  changes: VocabularyChange[];
}
/** Ce que la propagation a RÉELLEMENT poussé par le producteur — ou l'erreur nommée, rien d'écrit. */
export interface VocabularyPropagationResult { id: string; name: string; gln?: string; pushed?: number; note?: string; error?: string }

/**
 * L'état d'un magasin face au référentiel des communes.
 * ⚠️ TROIS SITUATIONS, PAS DEUX : à jour (`missing` nul), un écart à combler, ou une base qu'on n'a
 *    PAS PU LIRE (`error`) — cette dernière n'est surtout pas « à jour », et l'écran doit la
 *    distinguer, sinon un magasin injoignable passe pour un magasin servi.
 */
export interface CommunesStore {
  id: string; name: string; gln: string;
  /** Ce que la base porte déjà. */
  inStore: number;
  /** L'écart MESURÉ — ce qui lui serait envoyé. */
  missing: number;
  /** Magasin sans base : nommé, jamais compté comme à jour. */
  skipped?: string;
  /** Base illisible : le message RÉEL. */
  error?: string;
}

export interface CommunesAnalysis {
  /** Ce que la source officielle a rendu — un relevé sous le plancher fait refuser l'analyse. */
  source: { communes: number; pairs: number };
  stores: CommunesStore[];
}

export interface CommunesApplyResult { id: string; name: string; gln?: string; written?: number; note?: string; error?: string }

/** Configuration embarquee d un terminal (Mobile Partner) + magasins candidats et catalogue de droits. */
export interface DeviceConfigState {
  configurable: boolean;
  homeStoreId: string | null;
  defaultStoreId: string | null;
  storeOrder: string[];
  rights: string[];
  updatedAt: string | null;
  candidates: { id: string; name: string; gln: string; kind: string }[];
  availableRights: { code: string; label: string }[];
}

/** Base d'un serveur source de reprise (listée après connexion lecture seule). tableCount null = base non atteignable avec ce compte. */
export interface LegacyDatabase { name: string; sizeBytes: number; tableCount: number | null }
/** Table d'une base source analysée (phase 2). rowCount null = comptage impossible (table trop volumineuse / inaccessible). */
export interface LegacyTable { schema: string; name: string; rowCount: number | null; columnCount: number }
/**
 * Une ligne source non écrite, chiffrée et motivée. `lost` = introuvable dans la base cible ; sinon
 * l'information y est, portée par une autre étape (un doublon écarté n'est pas une perte).
 */
export interface LegacyDroppedRows { reason: string; count: number; lost: boolean }
/** Un désaccord entre les deux récits d'une même chose (mouvements ↔ inventaire, points ↔ carte). */
/** Un point de mesure d'espace disque : le volume, sa place, et QUAND on l'a regardé. */
/** Un système de fichiers relevé sur une machine. */
export interface DiskVolume {
  host: string;
  /** Le POINT DE MONTAGE mesuré — l'écran le nomme, sinon nul ne sait quel volume a été vu. */
  mount: string;
  device: string | null;
  fsType: string | null;
  at: string | null;
  /** `null` = NON MESURÉ, jamais « zéro octet libre ». Le motif réel est dans `error`. */
  freeBytes: number | null;
  totalBytes: number | null;
  error: string | null;
}

/** Une machine et ses volumes — ou son silence. */
export interface DiskHost {
  host: string;
  /** `null` = cette machine n'a JAMAIS déposé de relevé. L'écran le DIT au lieu de l'omettre. */
  lastAt: string | null;
  /** Vrai quand le dernier dépôt est trop vieux : l'agent est probablement arrêté. */
  stale: boolean;
  agentVersion?: string | null;
  agentStale: boolean;
  volumes: DiskVolume[];
}

export interface DiskState { volumes: number; intervalMs: number; hosts: DiskHost[] }
/**
 * L etat des sauvegardes d une machine.
 * ⚠️ QUATRE ETATS, ET AUCUN NE SE LIT « tout va bien » : jamais releve · perime · en echec · a jour.
 *    La PEREMPTION est le cas traitre — le dernier compte rendu dit « reussi », c est son AGE qui alarme.
 */
/**
 * UNE BASE dans un passage — son propre verdict.
 * ⚠️ UN TOTAL MASQUE UNE ABSENCE : cinq bases attendues, quatre sauvegardees, le total reste plausible
 *    et le magasin manquant ne se voit nulle part. C est cette ligne-la qui le rend visible.
 */
export interface BackupItem {
  database: string;
  ok: boolean;
  bytes?: number | null;
  /** La PENTE parle plus que la valeur : un dump qui triple finira par croiser l ouverture. */
  durationMs?: number | null;
  file?: string | null;
  error?: string | null;
}

export interface BackupHost {
  host: string;
  state: 'jamais' | 'perime' | 'echec' | 'ok';
  at?: string | null;
  archives?: number | null;
  bytes?: number | null;
  destination?: string | null;
  offsite: boolean;
  error?: string | null;
  /** Vide quand l agent est d une version qui n envoie pas encore le detail. */
  items: BackupItem[];
  agentVersion?: string | null;
  /** ⚠️ VRAI quand l agent de cette machine n est pas celui que le depot livre (Forme T). */
  agentStale: boolean;
  configSource?: string | null;
  nodeRole?: string | null;
}
/** Le reglage declare par la Console, applique par l agent au debut de son passage. */
export interface BackupConfigState {
  destinationKind: string;
  destination: string;
  hour: number;
  keep: number;
}

/** Une PIECE deposee : une base, une nuit, et ce qu elle a coute. */
export interface BackupPiece extends BackupItem {
  host: string;
  at: string;
  destination?: string | null;
  offsite: boolean;
}

/** ⚠️ `complete` DIT si la liste a ete bornee — une liste tronquee qui ressemble a une liste
    complete ferait conclure « il n y a que ca » le jour ou l on cherche une archive ancienne. */
export interface BackupHistory { pieces: BackupPiece[]; complete: boolean }

export interface BackupState {
  hosts: BackupHost[];
  config: BackupConfigState;
  /** La version que CE depot livre — la reference a laquelle l ecran compare. */
  agentVersion: string;
}

export interface LegacyDivergence { cle: string; libelle?: string; cumul: number; photo: number }
/**
 * La confrontation des deux sources d'une étape.
 * ⚠️ `divergencesNonListees` N'EST PAS DÉCORATIF : la liste est plafonnée pour l'écran, les COMPTES
 * restent exacts. Le taire ferait lire « voilà tout » à un arbitre qui n'en voit qu'une partie.
 */
export interface LegacyReconciliation {
  total: number; accords: number; divergences: LegacyDivergence[];
  divergencesNonListees: number; ecartNet: number; ecartAbsolu: number;
  /** Soldes négatifs par source — l’indice qui départage (une source qui en porte des milliers a dérivé). */
  negatifs: { photo: number; cumul: number };
}
/** Ligne du rapport de fidélité d'un import (phase 3). */
export interface LegacyImportTable {
  name: string; source: string; read: number; written: number; skipped: number;
  note?: string; error?: string;
  /** Détail de l'écart lecture→écriture — absent tant qu'une étape ne le chiffre pas. */
  dropped?: LegacyDroppedRows[];
  /** Le tableau sur lequel l'humain tranche — absent quand l'étape n'a qu'une source. */
  reconciliation?: LegacyReconciliation;
}
/** Rapport d'import legacy (dry-run ou écriture réelle). */
export interface LegacyImportReport { dryRun: boolean; database: string; storeGln: string; tables: LegacyImportTable[]; ok: boolean; error?: string }

/** Ligne d'identités & accès — compte staff (console_user). */
export interface StaffRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /** Nom d'affichage RECOMPOSÉ par le serveur — un seul endroit décide comment, pas chaque écran. */
  name: string;
  role: 'superadmin' | 'deploy_ops' | 'support_ops' | 'billing_ops';
  status: 'invited' | 'active' | 'disabled';
  lastLoginAt: string | null;
  createdAt: string;
  invitedByName: string | null; // qui a invité (résolu côté serveur), null pour le 1er compte / bris de glace
}

/** Santé d'un service surveillé (sonde en mémoire — latence réelle). */
/**
 * UNE BRIQUE d'un service — ce que sa réponse `/vN/ready` déclare, brique par brique.
 * ⚠️ `status` vaut `ok` ou le MESSAGE RÉEL de la panne : c'est lui qu'on affiche, pas un point rouge.
 */
export interface ServiceDep {
  name: string;
  /** Ce qu'est cette brique, en clair — déclaré par le SERVICE, jamais deviné ici. */
  kind?: string;
  status: string;
  /** Latence de CETTE brique (et non celle du service entier). */
  ms: number;
}

/**
 * LA MESURE DE LA SONDE, telle que le serveur la pratique : la fenêtre (relevés, minutes) et les paliers de latence.
 * ⚠️ L'écran la LIT pour sa légende, son sous-titre et les zones du graphique : recopiés ici, les paliers ont déjà
 *    divergé de la règle qui colore les tuiles.
 */
export interface HealthMeasure { samples: number; minutes: number; warnFromMs: number; highFromMs: number }

export interface ServiceHealth {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
  status: 'pending' | 'ok' | 'warn' | 'high' | 'error' | 'down';
  lastMs: number | null;
  lastCode: number | null; // dernier statut HTTP (pour distinguer « répond 404 » d'« injoignable »)
  /** MÉDIANE de la fenêtre — c'est elle qui décide la couleur (robuste à un pic isolé, TRANCHÉ 2026-08-11). */
  median: number | null;
  /** p95 : AFFICHÉ en repère sur la barre, ne commande plus l'état (à 10 relevés, il valait le maximum). */
  p95: number | null;
  uptime: number | null;
  /**
   * Les briques du service au dernier relevé.
   * ⚠️ `undefined` = PAS ENCORE MESURÉ (ou service n'exposant pas le détail) ; tableau VIDE = ce
   * service n'a réellement aucune dépendance. Les confondre ferait lire une lacune comme un fait.
   */
  deps?: ServiceDep[];
  samples: number;
  /** Durée couverte par la fenêtre : « % ok » ne doit jamais se lire comme une disponibilité absolue. */
  windowMinutes: number;
}
// ── Historique de latence (SA-9.1) ──
/** Un seau agrégé. ⚠️ `min/median/max` à null = AUCUN relevé abouti dans ce seau (injoignable de bout
 *  en bout), ce qui n'est pas « 0 ms ». `failures` compte les relevés SANS réponse. */
export interface HistoryBucket { t: number; min: number | null; median: number | null; max: number | null; samples: number; failures: number }
/** ⚠️ `buckets` ne contient QUE les seaux existants : un arrêt de la sonde laisse un TROU, que le rendu
 *  doit montrer comme tel — d'où `bucketSeconds`, qui permet de repérer la discontinuité. */
export interface History { buckets: HistoryBucket[]; bucketSeconds: number; hours: number; retentionDays: number }

/** Service surveillé (config éditable en Paramètres · Supervision). */
export interface MonitoredService { id: string; name: string; url: string; enabled: boolean }

/** Événement du journal d'authentification IdP (idp_audit). */
export interface IdpAuditEvent { id: number; action: string; at: string; ip: string | null; email: string | null; meta: unknown }
/** Application connectée (RP OIDC) déclarée dans l'IdP. */
export interface RpApp { clientId: string; name: string; redirectUris: string[]; grantTypes: string[] }
/** Fiche d'authentification IdP d'un compte (realm, statut, 2FA, dernière connexion). */
export interface IdpAccountInfo { exists: boolean; realm?: string; status?: string; twofa?: boolean; lastLoginAt?: string | null }
/** Ligne de la liste des comptes IdP — `realm` distingue le personnel Orgazone des comptes clients. */
export interface IdpAccountRow {
  email: string; name: string | null; realm: string;
  status: 'active' | 'invited' | 'disabled'; twofa: boolean;
  lastLoginAt: string | null; createdAt: string;
  firstName: string | null; lastName: string | null;
  /** Client de rattachement (§26.4quater) — `null` = aucun, ce qui est une anomalie pour un compte client. */
  tenantId: string | null; tenantName: string | null;
}
// ── Recherche transverse (rail du haut) ──
/** Résultats groupés par nature. `accountsError` = l'IdP n'a pas répondu : le groupe « comptes » n'est
 *  pas VIDE, il est INCONNU — l'écran doit le dire au lieu de laisser croire qu'on a cherché (§23.0). */
export interface SearchResults {
  query: string;
  tenants: { id: string; name: string }[];
  stores: { id: string; tenantId: string; name: string; gln: string; sCode: string | null }[];
  devices: { id: string; name: string | null; family: string; model: string | null; serial: string | null; storeName: string | null; tenantId: string | null }[];
  accounts: { email: string; name: string | null; realm: string; tenantId: string | null; tenantName: string | null }[];
  accountsError: string | null;
}

/** Grant RBAC : un scope inter-outils accordé à un compte (expiresAt = délégation bornée). */
export interface IdpGrant { id: string; scope: string; grantedBy: string | null; expiresAt: string | null; createdAt: string }

// ── Explorateur de bases (lecture seule, routé par l'annuaire) ──
/** Type d'affichage d'une colonne (rendu lisible : centimes→€, epoch→date, 0/1→oui/non ; brut au toggle). */
export type ColKind = 'id' | 'fk' | 'money' | 'fine' | 'rate' | 'epoch' | 'bool' | 'gln' | 'int' | 'text';
/** Base explorable (magasin en service ou agrégat) — le SPA choisit un `id`, jamais un DSN. */
export interface ExplorerBase { id: string; kind: 'store' | 'agg'; label: string; schema: string; gln: string | null }
/** Table d'une base explorée : comptage RÉEL (null = inconnu), verrou (append-only/projection), domaine. */
export interface ExplorerTable { name: string; rows: number | null; locked: boolean; group: string }
export interface ExplorerSchema { kind: 'store' | 'agg'; schema: string; label: string; lockWhy: string; groups: { g: string; tables: ExplorerTable[] }[]; tableCount: number }
/**
 * UNE RELATION RÉELLE de la base ouverte — une clé étrangère, avec ce qu’elle fait quand la cible bouge.
 * ⚠️ `onDelete` EST LE CŒUR DE LA VUE GRAPHIQUE : « cascade » dit ce qui PART AVEC une suppression,
 * « restrict » dit au contraire ce qui l’EMPÊCHE. Les deux se dessinent différemment, sinon le schéma
 * laisse croire qu’on peut effacer ce qui est verrouillé.
 */
export type FkAction = 'cascade' | 'setnull' | 'setdefault' | 'restrict' | 'noaction';
export interface ExplorerRelation {
  name: string;
  from: string;
  fromColumns: string[];
  to: string;
  toColumns: string[];
  onDelete: FkAction | string;
  onUpdate: FkAction | string;
  /**
   * La base IMPOSE-T-elle ce lien, ou le schéma le DÉCLARE-t-il seulement ?
   * ⚠️ Le dessin doit les distinguer : une contrainte est une garantie appliquée par Postgres, une
   * déclaration n'est qu'une intention. Les peindre pareil ferait croire à une intégrité que rien
   * ne tient.
   */
  enforced: boolean;
}
/** Un champ d'une table, tel qu'une tuile du graphe l'affiche. */
export interface ExplorerField {
  table: string;
  name: string;
  kind: ColKind;
  pk: boolean;
  nullable: boolean;
}
export interface ExplorerCol { k: string; kind: ColKind }
export interface ExplorerFilter { column: string; op: 'contains' | 'eq' | 'isnull' | 'notnull'; value?: string }
export interface ExplorerQuery { limit?: number; offset?: number; q?: string; sort?: string; direction?: 'asc' | 'desc'; filters?: ExplorerFilter[] }
/** Page triée et filtrée. `pk` identifie une clé simple pour les corrections ; null pour une clé composite. */
export interface ExplorerRows { table: string; pk: string | null; locked: boolean; cols: ExplorerCol[]; rows: (string | null)[][]; count: number; filteredCount: number; offset: number; limit: number; sort: string | null; direction: 'asc' | 'desc' }
/** Correction de masse tracée (SA-7.2) — audit + cible du rollback (valeurs d'origine conservées côté serveur). */
export interface ExplorerCorrection { id: string; at: string; byEmail: string; tableName: string; columnName: string; op: 'set' | 'prefix' | 'mul' | 'delete'; value: string; motif: string; affected: number; rolledBackAt: string | null }

/** Événement du journal d'activité (agrégé serveur depuis les journaux réels). */
export interface ActivityEvent { at: string; family: 'provisioning' | 'flotte' | 'blocages' | 'données' | 'clients' | 'plateforme'; text: string; meta: string }

// ── Kill-switch (SA-4) ──
/** Blocage (actif ou historisé) — cible résolue par le serveur, `tenantId` pour filtrer par client. */
export interface BlockRow {
  id: string; scope: 'subscription' | 'store' | 'device'; level: 'warn' | 'lock';
  reason: string; source: string; targetId: string; label: string; sub: string; tenantId: string | null;
  /** Auteur en « Prenom Nom », resolu a la lecture ; repli sur la valeur brute (regle automatique, compte supprime). */
  createdBy: string; createdAt: string; liftedAt: string | null; liftedBy: string | null; liftReason: string | null;
  /** DÉCIDÉ ≠ APPLIQUÉ : la consigne est-elle RÉELLEMENT parvenue aux caisses (canal `blockade`) ? */
  propagated: boolean;
  /** Idem pour la levée — une caisse déverrouillée ici mais toujours bloquée sur place = magasin à l'arrêt. */
  liftPropagated: boolean;
}

/** Une destination que la descente n'a pas atteinte — le motif est RÉEL, il vient du BO. */
// ── Rôles et droits (modèle vivant) ──
/** Niveau d'accès à un module. ⚠️ Un module absent d'une carte vaut `none` — jamais « hérité ». */
export type RightLevel = 'none' | 'read' | 'write';
export interface AccessModuleDto {
  key: string; label: string; note: string;
  /** Gradué : « lecture » a un sens. Sinon le module est tout-ou-rien et `write` se LIT « accès ». */
  graded: boolean;
}
export interface RoleRow {
  code: string; app: 'bo' | 'erp'; appLabel: string; name: string; description: string;
  locked: boolean; rights: Record<string, RightLevel>;
  /** Clients qui SUIVENT le modèle — déduit (= sans personnalisation), jamais stocké. */
  follow: number;
  /** Clients ÉPINGLÉS : ils ne reçoivent plus vos modifications. */
  custom: number;
  updatedAt: string; updatedBy: string | null;
}
export interface RoleDiffLine { module: string; label: string; from: RightLevel; to: RightLevel; fromLabel: string; toLabel: string }
export interface RoleDetail extends Omit<RoleRow, 'follow' | 'custom'> {
  modules: AccessModuleDto[];
  customised: { tenantId: string; tenantName: string; pinnedAt: string; pinnedBy: string | null; reason: string | null; diff: RoleDiffLine[] }[];
}

export interface BlockPropagation { propagated: boolean; failures: { gln: string; error: string }[] }

// ── Compte d'administration des caisses (§26.4bis) ──
/** État du compte, SANS le hachage : ce qui intéresse est « où en est la descente », jamais le secret. */
// ── COMPTES CLIENTS (realm CLIENTS de l'IdP, rattachés à un client) ──────────────────────────────
/** Accès accordé à un compte : `<outil>:<rôle>[:<GLN>]`. */
export interface TenantGrant { id: string; scope: string; grantedBy: string | null; expiresAt: string | null; createdAt: string }
export interface TenantAccount {
  email: string;
  firstName: string;
  lastName: string;
  /** Recomposé par le serveur (voir StaffRow.name). */
  name: string;
  invitedAt: string;
  invitedBy: string | null;
  /** Ce que l'IdP sait du compte — `null` s'il est injoignable (voir `idpError`), jamais remplacé par un statut inventé. */
  identity: { exists: boolean; realm?: string; status?: string; twofa?: boolean; lastLoginAt?: string | null } | null;
  grants: TenantGrant[];
  /** Message RÉEL de l'IdP quand la lecture a échoué — l'écran le dit au lieu d'afficher un compte incomplet. */
  idpError: string | null;
}
/** Ce qui est attribuable CHEZ CE CLIENT. Les outils fermés sont présents, avec leur motif. */
export interface TenantAccessOptions {
  /** `licensed` = sous licence : information COMMERCIALE, jamais une condition d'accès. */
  tools: { key: string; label: string; app: 'bo' | 'erp'; perStore: boolean; licensed: boolean }[];
  stores: { gln: string; name: string }[];
  roles: { code: string; name: string; app: string }[];
}

export interface AdminAccountState {
  exists: boolean;
  label?: string;
  rotation?: number;
  rotatedAt?: string;
  rotatedBy?: string | null;
  isActive?: boolean;
  /** Le PIN Mobile Partner du même compte — sa rotation est indépendante du mot de passe des caisses. */
  mobilePin?: { exists: boolean; rotation: number; rotatedAt?: string | null; rotatedBy?: string | null };
  /** Magasins en service (donc destinataires possibles). */
  stores: number;
  /** Magasins ayant RÉELLEMENT reçu la rotation courante — c'est le « 42 » de « 42/47 ». */
  delivered: number;
  /** Ceux qui ne l'ont pas reçue, NOMMÉS : l'ancien mot de passe y fonctionne encore. */
  pending: { gln: string; name?: string | null; error: string | null }[];
}
export interface AdminAccountResult { rotation: number; delivered: number; total: number; failures: { gln: string; error: string }[] }

// ── Migrations multi-base (SA-6) ──
/**
 * Une base magasin vue par l'orchestrateur.
 * ⚠️ DEUX MESURES, ET `upToDate` LES EXIGE TOUTES LES DEUX (correctif 2026-08-17) : la `version` dit
 * quelles migrations NUMÉROTÉES ont tourné ; `missingTables` dit ce qui manque réellement. Une table
 * neuve arrive par la RÉCONCILIATION, qui ne fait pas bouger le numéro — une base pouvait donc être
 * verte tout en n'ayant jamais reçu quatre tables, et l'écran qui les lit échouait des semaines plus tard.
 */
export interface MigrationStoreState { id: string; name: string; gln: string; tenantId: string | null; tenantName: string | null; version: number | null; upToDate: boolean; missingTables?: string[]; missingColumns?: string[];
  /** Colonnes PRÉSENTES mais au MAUVAIS TYPE — invisibles à la sonde de présence (incident 2026-09-03). */
  wrongTypes?: string[]; error?: string }
/** Contrôle du PRÉ-VOL de migration (mesure réelle sur la base : latence, verrous, vacuum…). */
export interface HealthCheck { id: string; label: string; status: 'ok' | 'warn' | 'fail'; detail: string }
/** Bilan de santé d'une base avant migration ; `overall` = pire contrôle (fail bloque le lancement). */
export interface StorePreflight { id: string; name: string; gln: string; overall: 'ok' | 'warn' | 'fail'; checks: HealthCheck[] }
/** Étape versionnée du schéma magasin (réelle, depuis @orgazone/retail-schema). */
export interface MigrationStep { version: number; label: string; statements: number }
/** `codeVersion` : le commit de l'image en service (`null` quand l'environnement n'en déclare pas). */
export interface MigrationsStatus { target: number; codeVersion: string | null; migrations: MigrationStep[]; stores: MigrationStoreState[] }
/** Rapport d'application par base : fromVersion=null = base injoignable (rien tenté) ; snapshot = nom de
 *  l'instantané créé avant migration (si demandé). */
export interface MigrationRunResult { id: string; name: string; gln: string; snapshot?: string; fromVersion: number | null; toVersion?: number; applied?: { version: number; label: string }[]; error?: string }
export interface MigrationsRunReport { target: number; results: MigrationRunResult[] }
/** Instantané d'une base magasin (copie intégrale pré-migration) — la version d'origine et la date vivent dans le nom. */
export interface SnapshotRow { name: string; db: string; fromVersion: number; at: number; sizeBytes: number; store: { id: string; name: string; gln: string } | null }

/**
 * Ce qu'une purge de journal a RÉELLEMENT fait, magasin par magasin.
 *
 * ⚠️ `refused` N'EST PAS UNE LISTE D'ERREURS, et l'écran doit la montrer comme telle : ce sont les
 * canaux que la purge s'est INTERDIT de toucher, avec leur motif. Sans eux, un rapport à zéro se lit
 * « rien à purger » alors qu'il veut dire « on s'est interdit de purger » — et l'exploitant cherche
 * une panne là où une garde fait son travail.
 * ⚠️ `ignoredDevices` compte les caisses devenues muettes depuis trop longtemps : ce sont elles qui
 * ont cessé de retenir le journal, et elles rechargeront tout à leur retour.
 */
export interface PurgeJournalStore {
  gln: string;
  ok: boolean;
  totalRemoved?: number;
  purged?: { channel: string; removed: number; through: string }[];
  refused?: { channel: string; reason: string }[];
  ignoredDevices?: number;
  error?: string;
}
export interface PurgeJournalReport { ok: boolean; totalRemoved: number; results: PurgeJournalStore[] }

// Erreur API typée. `code` = CLÉ machine du motif (§16.2.7 `message`), `status` = HTTP réel,
// `details` = erreurs par champ ({field, code}) quand le serveur les fournit.
// ⚠️ Le client HTTP n'est PAS réimplémenté ici : il vient de la PRIMITIVE PARTAGÉE
// `@orgazone/api-envelope/client`, pendant exact de `applyEnvelope` côté serveur — les deux côtés du
// contrat §16.2.7 sortent du même package et ne peuvent donc plus diverger (cf. SPEC §16.2.7bis : trois SPA
// avaient recopié ce client, l'une lisait `/v1/auth/*` hors enveloppe → connexion impossible).
export { ApiError } from '@orgazone/api-envelope/client';
import { createApiClient } from '@orgazone/api-envelope/client';

// Libellés FR des clés `message` courantes (le serveur ne renvoie JAMAIS de texte traduit : il envoie la clé,
// le client fait le lookup — §16.2.7). Clé inconnue → on affiche la clé elle-même (honnête, jamais inventé).
const MESSAGE_FR: Record<string, string> = {
  unauthorized: 'Authentification requise.',
  forbidden: 'Droits insuffisants pour cette action.',
  not_found: 'Ressource introuvable.',
  route_not_found: 'Route inconnue.',
  conflict: 'Conflit avec l’état actuel.',
  validation_failed: 'Données invalides.',
  bad_request: 'Requête invalide.',
  rate_limited: 'Trop de requêtes — réessayez dans un instant.',
  internal_error: 'Erreur interne du serveur.',
  // Réinitialisation de base — les motifs qui doivent atteindre l’écran tels quels.
  confirmation_mismatch: 'La recopie ne correspond pas au nom du magasin.',
  drop_failed: 'La base n’a pas pu être supprimée.',
  reprovision_failed: 'Base supprimée, mais sa recréation a échoué.',
  payload_too_large: 'Contenu trop volumineux.',
};
const humanize = (key: string, fallback?: string): string => MESSAGE_FR[key] ?? fallback ?? key;

// Client de la console : traduction FR des clés + politique 401 propre à l'app (session expirée en cours
// d'usage → on recharge, `RequireAuth` ré-authentifie via SSO IdP / login local).
// 401 sur une route métier = session expirée en cours d'usage → on repart à l'accueil, que la garde de
// route renverra vers la connexion. ⚠️ Les routes /auth/* en sont exemptées par le client partagé
// (`isAuthRoute`) : sans cela, le 401 NORMAL de /auth/me au boot déclenchait cette redirection, donc un
// rechargement, donc un nouveau /auth/me — une boucle de rechargement pour tout visiteur non connecté.
const client = createApiClient({
  humanize,
  // Politique 401 COMMUNE aux trois surfaces (@orgazone/ui-web) : garde la destination, repasse par
  // la garde de route, et ne rebondit pas pour quelqu'un qui n'a jamais eu de session.
  onUnauthorized: bounceOnUnauthorized,
});

/** Ergonomie historique de la console : `req(path, init)`. Le corps est déjà sérialisé par l'appelant. */
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  return client.request<T>((init?.method ?? 'GET').toUpperCase(), path, undefined, init);
}

/** Ce qu'un écran envoie pour enregistrer un gabarit — la forme, pas la règle (elle est au serveur). */
export interface EmailTemplateInput {
  subject: string;
  /** ⚠️ `null` = PAS de titre. Une chaîne vide ferait poser un titre blanc en tête du message. */
  title: string | null;
  body: string;
  button: { label: string; url: string } | null;
}

/** Un champ de fusion proposé par un usage — son nom EST sa clé, il ne se traduit pas. */
export interface EmailMergeField {
  name: string;
  description: string;
  fallback: string;
  /** ⚠️ Le serveur REFUSE un gabarit qui l'oublie : sans lui, le message ne veut rien dire. */
  required?: boolean;
}

/** Un gabarit d'éditeur tel que l'écran le relit. */
export interface ConsoleEmailTemplate extends EmailTemplateInput {
  purpose: string;
  /** `false` = aucune ligne en base, c'est le courrier du LOGICIEL qui part. */
  custom: boolean;
  /**
   * 🔴 POURQUOI CE TEXTE N'EST PAS CELUI QUI PART, quand il est enregistré mais plus valide.
   * ⚠️ Le taire ferait afficher « texte personnalisé » pendant que le courrier d'origine s'en va.
   */
  rejected: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  fields: EmailMergeField[];
  /** ⚠️ Le courrier d'origine voyage avec : « revenir au courrier d'origine » doit pouvoir se MONTRER. */
  fallback: EmailTemplateInput;
}

/** Une annonce signalée par la veille des défaillances (SA-9.3), en attente de décision. */
export interface LegalSignal {
  id: string; tenantId: string; tenantName: string; source: 'bodacc' | 'sirene'; siren: string;
  /** La nature EXACTE du jugement, telle que publiée. */
  nature: string; court: string | null; judgmentDate: string | null; publishedOn: string | null; url: string | null;
}
/** Une annonce de l'historique d'un client — signalée ou non, avec la décision prise. */
export interface LegalEventRow {
  id: string; source: 'bodacc' | 'sirene';
  /** L'identifiant de l'annonce à sa source (« A202699000001 » au BODACC) et le SIREN interrogé. */
  externalId: string; siren: string;
  nature: string; court: string | null; judgmentDate: string | null; publishedOn: string | null;
  url: string | null; signal: boolean; decision: LegalDecision | null; reason: string | null; decidedBy: string | null; decidedAt: string | null;
}
export type LegalDecision = 'ignore' | 'warn' | 'contact';
/** Un client signalé par la veille — la pastille de la liste des clients. `bodacc` : au moins une annonce du BODACC. */
export interface LegalFlag { tenantId: string; open: number; bodacc: boolean }
/** Les annonces d'un client, et ce qui permet de dire « aucune » honnêtement : surveillé ou non, et depuis quand. */
export interface TenantLegalView { watched: boolean; lastRunAt: string | null; events: LegalEventRow[] }
/** La veille : ses signalements ouverts, les clients qu'elle ne peut pas surveiller, et son dernier passage (`null` : jamais). */
export interface LegalWatchView {
  open: LegalSignal[];
  /** Les clients qui portent un signalement, tranché ou non (`open` : combien attendent leur décision). */
  flagged: LegalFlag[];
  unwatched: { id: string; name: string }[];
  lastRun: { at: string; watched: number; unwatched: number; newEvents: number; errors: { siren: string; source: string; error: string }[] } | null;
}

/** Répartitions RÉELLES du tableau de bord (annuaire). */
export interface DashboardData {
  tenants: { total: number; mono: number; multi: number; aggregates: number };
  stores: { total: number; provisioning: number; active: number; depots: number };
  devices: { total: number; available: number; assigned: number; rentalCourte: number; rentalLongue: number; purchase: number };
  staff: number;
}

export const api = {
  // Auth
  // Mode d'auth du service — contrat de plateforme servi par @orgazone/auth-web (type NON redéclaré ici :
  // le redéclarer autoriserait une dérive silencieuse ; c'est précisément ce qui était arrivé avec `armed`).
  mode: () => req<AuthMode>('/v1/auth/mode'),
  // Tableau de bord
  dashboard: () => req<DashboardData>('/v1/dashboard'),
  me: () => req<{ user: User }>('/v1/auth/me'),
  login: (email: string, password: string, remember: boolean) =>
    req<{ user?: User; status?: string }>('/v1/auth/login', { method: 'POST', body: JSON.stringify({ email, password, remember }) }),
  logout: () => req<{ ok: true }>('/v1/auth/logout', { method: 'POST' }),
  // Reinitialisation du mot de passe du compte de SECOURS local. ⚠️ `forgot` repond toujours 200 (il ne
  // revele pas si l adresse existe) : l ecran ne peut donc PAS affirmer qu un e-mail est parti.
  forgotPassword: (email: string) => req<{ ok: true }>('/v1/auth/password/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token: string, password: string) => req<{ ok: true }>('/v1/auth/password/reset', { method: 'POST', body: JSON.stringify({ token, password }) }),
  // Annuaire (lecture) — gaté console.read côté serveur.
  tenants: () => req<{ tenants: TenantRow[] }>('/v1/annuaire/tenants'),
  tenant: (id: string) => req<TenantDetail>(`/v1/annuaire/tenants/${id}`),
  // Modifier un client (super-admin) : les valeurs voulues, et celles que le formulaire a ouvertes — le serveur refuse
  // (409) un client changé entre-temps. La réponse dit ce que chaque magasin a reçu de l'identité.
  updateTenant: (id: string, body: { values: TenantIdentity; base: TenantIdentity }) =>
    req<{ ok: true; changes: { field: string; before: string | null; after: string | null }[]; stores: TenantStoreResend[] }>(
      `/v1/annuaire/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  drafts: () => req<{ drafts: DraftRow[] }>('/v1/annuaire/drafts'),
  // Relecture d'un brouillon : statut réel (to_correct = complément demandé, validated = passé au wizard).
  updateDraft: (id: string, status: 'to_correct' | 'validated') =>
    req<{ ok: true }>(`/v1/annuaire/drafts/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  devices: () => req<{ devices: DeviceRow[] }>('/v1/annuaire/devices'),
  // Flotte — ajouter un appareil au POOL (inventaire non affecté), réservé deploy_ops/super-admin. Le nom est exigé.
  addDevice: (body: { name: string; equipmentType: string; model?: string; serial?: string }) =>
    req<{ device: { id: string } }>('/v1/annuaire/devices', { method: 'POST', body: JSON.stringify(body) }),
  // Flotte — modifier un appareil : son nom (exigé), son type, son modèle, son numéro de série (`null` efface). ⚠️ Le
  // serveur refuse de changer la NATURE d'un appareil affecté (409), et toute modification d'un appareil révoqué.
  editDevice: (id: string, body: { name: string; equipmentType: string | null; model: string | null; serial: string | null }) =>
    req<{ ok: true; changed: number }>(`/v1/annuaire/devices/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  // Flotte — corriger le régime et les dates d'un appareil affecté, sans retour au parc. `lifted` : le blocage automatique de
  // location devenu sans objet a été levé, et sa levée a-t-elle atteint les caisses.
  correctRental: (id: string, body: { rentalType: 'courte' | 'longue' | 'achat'; start: string | null; end: string | null }) =>
    req<{ ok: true; changes: { field: string; before: string | null; after: string | null }[]; lifted: { propagated: boolean; failures: { gln: string; error: string }[] } | null }>(
      `/v1/annuaire/devices/${id}/rental`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Flotte — affecter (à un magasin + régime), retirer (retour pool) ou révoquer un appareil.
  deviceAction: (id: string, body: { action: 'assign' | 'unassign' | 'revoke'; storeId?: string; rentalType?: string; rentalStart?: string; rentalEnd?: string }) =>
    req<{ ok: true }>(`/v1/annuaire/devices/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Flotte — supprimer un appareil du parc. ⚠️ Le serveur ne l'accepte que pour un appareil RÉVOQUÉ (409 sinon).
  deleteDevice: (id: string) => req<{ ok: true }>(`/v1/annuaire/devices/${id}`, { method: 'DELETE' }),
  // Mise en service — état d'appairage courant (jeton actif à afficher + dernière redemption réelle).
  devicePairing: (id: string) => req<DevicePairingState>(`/v1/annuaire/devices/${id}/pairing`),
  // Configuration embarquee (Mobile Partner) : magasins accessibles ordonnes, defaut, droits du terminal.
  deviceConfig: (id: string) => req<DeviceConfigState>(`/v1/annuaire/devices/${id}/config`),
  saveDeviceConfig: (id: string, body: { defaultStoreId: string; storeOrder: string[]; rights: string[] }) =>
    req<{ ok: true }>(`/v1/annuaire/devices/${id}/config`, { method: 'PUT', body: JSON.stringify(body) }),
  // Mise en service — émettre un QR (jeton usage unique 24 h ; invalide tout jeton non consommé antérieur).
  createPairing: (id: string) => req<DevicePairingActive>(`/v1/annuaire/devices/${id}/pairing`, { method: 'POST' }),
  // Reprise legacy (phase 1) — connexion LECTURE SEULE au serveur source (Postgres) + listing des bases.
  // ⚠️ identifiants transmis à chaque appel (jamais persistés côté serveur).
  legacyDatabases: (body: { driver: string; host: string; port?: string; user: string; password: string; ssl?: boolean; sslInsecure?: boolean }) =>
    req<{ serverVersion: string; databases: LegacyDatabase[] }>('/v1/legacy/databases', { method: 'POST', body: JSON.stringify(body) }),
  // Reprise legacy (phase 2) — analyse d'une base source : inventaire des tables + comptage des lignes (prévisionnel).
  legacyAnalyze: (body: { driver: string; host: string; port?: string; user: string; password: string; ssl?: boolean; sslInsecure?: boolean; database: string }) =>
    req<{ tables: LegacyTable[]; totalRows: number }>('/v1/legacy/analyze', { method: 'POST', body: JSON.stringify(body) }),
  // Reprise legacy (phase 3a) — import du cluster fondational vers la base du magasin. dryRun=true : compte, n'écrit rien.
  /** Suivi VIVANT d'une reprise en cours : les tables DÉJÀ terminées, avec leurs comptes réels. */
  legacyImportProgress: (storeId: string) =>
    // ⚠️ `report` PORTE L'ISSUE, pas seulement l'avancement : une reprise dépasse les 300 s du balanceur,
    // qui coupe alors la requête d'origine par un 504 pendant que le travail se termine normalement.
    // C'est par ce relevé que l'écran apprend ce qui s'est réellement passé.
    req<{
      running: boolean; done: boolean; startedAt: number | null;
      tables: LegacyImportReport['tables']; bootedAt: number;
      report: LegacyImportReport | null; error: string | null;
    }>(`/v1/legacy/import/progress/${encodeURIComponent(storeId)}`),
  legacyImport: (body: {
    driver: string; host: string; port?: string; user: string; password: string; ssl?: boolean;
    sslInsecure?: boolean; database: string; storeId: string; dryRun: boolean;
    /** Qui fait foi quand les deux sources divergent — `photo` = inventaire/carte, `cumul` = grand livre. */
    arbitrage?: { stock: 'photo' | 'cumul'; fidelite: 'photo' | 'cumul' };
  }) =>
    req<LegacyImportReport>('/v1/legacy/import', { method: 'POST', body: JSON.stringify(body) }),
  // Provisioning — créer un client (réservé super-admin côté serveur). Identité complète (wizard) optionnelle.
  createTenant: (body: {
    name: string; siren?: string; businessKind?: string; topology: 'mono' | 'multi';
    commercialName?: string; siretHq?: string; naf?: string; legalForm?: string;
    address?: { voie?: string; cp?: string; ville?: string }; contact?: { nom?: string; email?: string; tel?: string };
    vatFranchise: boolean; vatNumberAssigned: boolean;
  }) => req<{ tenant: { id: string; name: string } }>('/v1/annuaire/tenants', { method: 'POST', body: JSON.stringify(body) }),
  // Provisioning — SUPPRIMER un client + ses magasins/bases + libérer ses appareils (réservé super-admin, destructif).
  /**
   * RÉINITIALISER la base d'un magasin : la dropper puis la recréer VIERGE.
   * ⚠️ DESTRUCTIF ET IRRÉVERSIBLE — données fiscales comprises, aucune sauvegarde prise.
   * ⚠️ `confirm` doit être le NOM EXACT du magasin : le serveur le revérifie. Une protection qui ne
   * vit que dans le navigateur ne protège que du geste maladroit, jamais de l'appel direct.
   */
  /** Provenance des reprises legacy d'un magasin (migration 0028) — sans le rapport intégral. */
  storeImports: (id: string) =>
    req<{ runs: {
      id: string; dryRun: boolean; ok: boolean; error: string | null;
      sourceHost: string; sourcePort: number; sourceDatabase: string; sourceUser: string;
      arbitrageStock: string | null; loyaltyArbitration: string | null;
      rowsRead: number; rowsWritten: number; tablesInError: number;
      seeded: number | null; seedError: string | null;
      startedAt: string; finishedAt: string; createdBy: string | null;
    }[] }>(`/v1/annuaire/stores/${encodeURIComponent(id)}/imports`),
  /** Relance l'amorçage du journal de sync (idempotent — IDs `seed:` déterministes, rien n'est réécrit). */
  reseedStoreJournal: (id: string) =>
    req<{ ok: true; seeded: number; rejournaled: number }>(`/v1/annuaire/stores/${encodeURIComponent(id)}/seed-journal`, { method: 'POST' }),
  /** Refait descendre aux caisses, en entier, les fiches reprises et les référentiels propagés (réparation). */
  reemitStoreChannels: (id: string) =>
    req<{ ok: true; emitted: number; channels: { channel: string; emitted: number }[] }>(`/v1/annuaire/stores/${encodeURIComponent(id)}/reemit`, { method: 'POST' }),
  /** Réinitialise la base — refusée dès la première vente ; le motif entre au journal de la Console. */
  resetStoreDb: (id: string, confirmation: string, reason: string) =>
    req<{ ok: true; name: string; dbName?: string; seededRefs: number; mediaRemoved: number | null; mediaError?: string; boDeclared?: boolean; boError?: string; identityMissing?: string[]; identityError?: string }>(`/v1/annuaire/stores/${encodeURIComponent(id)}/reset-db`, { method: 'POST', body: JSON.stringify({ confirm: confirmation, reason }) }),
  /**
   * SUPPRIME un magasin seul — son client reste.
   * ⚠️ `purgeBackups` détruit aussi les INSTANTANÉS et le rôle Postgres, donc la dernière copie du
   *    magasin une fois sa base partie. L'écran ne l'envoie qu'après avoir proposé la sauvegarde.
   */
  deleteStore: (id: string, confirmation: string, purgeBackups: boolean, reason: string) =>
    req<{ ok: true; name: string; gln: string; devicesReleased: number; snapshotsPurged: number }>(
      `/v1/annuaire/stores/${encodeURIComponent(id)}`,
      { method: 'DELETE', body: JSON.stringify({ confirm: confirmation, purgeBackups, reason }) }),
  /**
   * LA SAUVEGARDE TÉLÉCHARGEABLE d'un magasin — par la primitive `download` partagée.
   * ⚠️ POST et non GET : elle lance un `pg_dump` complet et laisse une trace nominative au journal.
   *    Le dépôt a déjà payé cette leçon (`GET /admin-account` distribuait le compte d'administration
   *    à toute la flotte, déclenché par un rafraîchissement) : un préchargement de navigateur ne doit
   *    pas pouvoir exporter une base magasin.
   * ⚠️ Jamais un `fetch` à la main dans l'écran : le CSRF, le cookie de session et la politique 401
   *    vivent dans `download`, et une copie oublie toujours l'un des trois.
   */
  storeDump: (id: string) => client.download('POST', `/v1/annuaire/stores/${encodeURIComponent(id)}/dump`),

  // ── LA REPRISE DEPUIS UN AUTRE MAGASIN DU CLIENT ─────────────────────────────────────────────
  // Le geste appartient au staff, qui crée les magasins ; le MOTEUR de copie vit au BO (il connaît les bases et le
  // registre de ce qui se reprend), la Console demande. ⚠️ Le cloisonnement (même client) est vérifié DEUX FOIS : ici
  // sur l annuaire, et par le BO sur son registre — copier le paramétrage d un client vers un autre serait une fuite.
  // ⚠️ Les formes viennent du contrat partagé avec le BO (`store-takeover.ts`) : les deux moitiés lisent le même type.
  /** Ce qui est reprenable — le registre vient du BO, jamais une liste tenue à l écran. */
  takeoverItems: () => req<{ items: TakeoverItem[] }>('/v1/annuaire/takeover/items'),
  /** Les autres magasins du client, en service, proposables comme source. */
  takeoverSources: (id: string) => req<{
    target: { id: string; gln: string | null };
    sources: { id: string; name: string; gln: string | null; sCode: string | null; status: string }[];
  }>(`/v1/annuaire/stores/${encodeURIComponent(id)}/takeover/sources`),
  /** Ce que la reprise déplacerait — obligatoire avant d appliquer (la copie écrase ce qu elle recouvre). */
  takeoverPreview: (id: string, sourceId: string) => req<TakeoverPreview>(`/v1/annuaire/stores/${encodeURIComponent(id)}/takeover/preview?source=${encodeURIComponent(sourceId)}`),
  /** Applique la reprise — le rapport dit ce qui a RÉELLEMENT été repris, item par item. */
  /** Un appel de reprise : sans `after`, le premier ; avec, le lot de fiches qui suit ce curseur. */
  takeoverApply: (id: string, sourceId: string, items: string[], after: string | null = null) =>
    req<TakeoverReport>(`/v1/annuaire/stores/${encodeURIComponent(id)}/takeover`, { method: 'POST', body: JSON.stringify({ source: sourceId, items, ...(after ? { after } : {}) }) }),

  deleteTenant: (id: string) =>
    req<{ ok: true; name: string; storesDeleted: number; devicesReleased: number; dbsDropped: string[]; dbErrors: string[] }>(`/v1/annuaire/tenants/${id}`, { method: 'DELETE' }),
  // Sirene — autocomplétion de l'identité depuis l'annuaire officiel des entreprises (proxifié serveur).
  sireneSearch: (q: string) => req<{ results: SireneResult[] }>(`/v1/sirene/search?q=${encodeURIComponent(q)}`),
  // Annuaire magasins + provisioning d'un magasin (mise en service, réservé deploy_ops/super-admin).
  stores: () => req<{ stores: StoreRow[] }>('/v1/annuaire/stores'),
  createStore: (body: { tenantId: string; name: string; gln: string; kind?: 'store' | 'depot'; businessKind?: string; siret?: string; syncMode?: 'mono' | 'bi'; currency?: string; address?: { voie?: string; cp?: string; ville?: string } }) =>
    req<{ store: { id: string; sCode: string; radicalEan: string } }>('/v1/annuaire/stores', { method: 'POST', body: JSON.stringify(body) }),
  // Mise en service : crée la base Postgres du magasin (tronc + gardes NF525) + sème la config initiale
  // (settings : réglages/intégrations du wizard), statut provisioning → active.
  // `seededRefs` = lignes de référentiels RÉELLEMENT écrites dans la base (moyens de paiement, motifs,
  // grilles… selon le type de commerce) ; `refsNote` dit pourquoi le métier n'a pas été résolu, le cas échéant.
  // ⚠️ Le type declarait TROIS champs sur huit : `boDeclared`/`boError`/`ediDeclared`/`ediError` etaient
  // renvoyes par le serveur et jetes ici. Une declaration au BO ou a l EDI qui ECHOUE passait donc en
  // vert dans le wizard, sans un mot — un echec avale (§23.0). Le contrat porte desormais le rapport
  // COMPLET, et l ecran le rend.
  provisionStore: (id: string, settings?: Record<string, string>) =>
    req<ProvisionReport>(`/v1/annuaire/stores/${id}/provision`, { method: 'POST', body: settings ? JSON.stringify({ settings }) : undefined }),
  // Réglages ciblés d'un magasin en service (allowlist §13.1quater : seuils de synchro périodique).
  // ── Référentiels de préparamétrage (SA-10.1) — listes maîtres niveau éditeur. ──
  // ── Sécurité · liens (SA-10.1) : durées de validité. `pairingTtlHours` est APPLIQUÉE par la Console
  // (relue à chaque QR émis) ; `idp` = durées en vigueur côté IdP, en lecture seule (réglées là-bas).
  linkSettings: () => req<{ pairingTtlHours: number; bounds: { min: number; max: number }; draftInviteTtlHours: number; draftInviteBounds: { min: number; max: number }; idp: { inviteTtlMs: number; resetTtlMs: number } | null; idpError: string | null }>('/v1/settings/links'),
  // Messagerie sortante (SA-10.5) : lecture, ecriture, et ENVOI DE TEST — le seul moyen de SAVOIR.
  mailSettings: () => req<MailSettings>('/v1/settings/mail'),
  updateMailSettings: (v: Partial<MailSettings> & { secret?: string }) =>
    req<MailSettings>('/v1/settings/mail', { method: 'PUT', body: JSON.stringify(v) }),
  testMail: (to: string) => req<{ ok: boolean; error?: string; effective: string }>('/v1/settings/mail/test', { method: 'POST', body: JSON.stringify({ to }) }),
  // ── Reglages du BACK-OFFICE (2026-08-30). ⚠️ La Console DEMANDE au back-office, elle n ecrit jamais
  // dans sa base : les deux services peuvent vivre sur deux machines. Aucune base client n est touchee.
  boSettings: () => req<BoSettingsState>('/v1/settings/backoffice'),
  /** Éprouve le compte maître SMS Envoi — l'appel réel est fait par le back-office, qui a le secret. */
  /**
   * Éprouve le compte ESENDEX — l'appel réel est fait par le back-office, qui détient la clé.
   * ⚠️ UNE SEULE capacité : la même clé ouvre l'envoi, le solde et la délégation.
   */
  testBoEsendex: () => req<{
    ok: boolean; error?: string;
    probe?: {
      ok: boolean; accountReference?: string; baseUrl?: string;
      /** Solde **en chaîne** (cinq décimales). Absent = compte post-payé, pas un solde nul. */
      available?: string; reserved?: string; error?: string;
    };
  }>('/v1/settings/backoffice/esendex/test', { method: 'POST' }),
  /**
   * Éprouve le compte SMS Envoi — l'appel réel est fait par le back-office, qui a les secrets.
   * ⚠️ DEUX CAPACITÉS, PAS UN VERDICT : SMS Envoi expose deux API et un compte peut avoir l'une sans
   * l'autre. **envoi** = HTTP v2 (e-mail + clé, sans mot de passe) ; **revente** = REST v1.0
   * (sous-comptes, crédits). `ok` vaut « au moins une répond », jamais « tout marche ».
   */
  testBoSmsEnvoi: () => req<{
    ok: boolean; error?: string;
    probe?: {
      ok: boolean;
      envoi: { ok: boolean; account?: string; error?: string };
      revente: {
        ok: boolean; account?: string; baseUrl?: string; error?: string;
        mode?: 'jeton' | 'identifiants';
        /** La clé utilisateur — à reporter dans « Jeton d'API » pour supprimer le mot de passe. */
        userKey?: string;
        money?: number; credits?: Array<{ type: string; quantity: number }>;
        subaccounts?: number; subaccountsError?: string;
      };
    };
  }>('/v1/settings/backoffice/smsenvoi/test', { method: 'POST' }),
  // ── LES COMPTES D'ENVOI DES MAGASINS (§11.1) — rattacher, créer, prouver.
  // ⚠️ DEUX FAITS, DEUX NOMS. `hasSecret` (compte enregistré) = « nous détenons un mot de passe »,
  //    fait LOCAL ; `canSend` (épreuve) = « l'opérateur nous a authentifiés », fait DISTANT. Un compte
  //    RATTACHÉ n'est pas un compte QUI PEUT ENVOYER : l'opérateur débite le compte authentifié, et sa
  //    liste ne donne que des identités. Les nommer pareil ferait croire à un canal branché (§23.0).
  /** Les sous-comptes du compte maître, croisés avec les magasins déjà rattachés. */
  boSubaccounts: () => req<{
    ok: boolean; error?: string; account?: string; unidentified?: number;
    subaccounts?: Array<{ externalId: string | null; login: string | null; label: string | null;
      known: boolean; stores: Array<{ gln: string; name: string | null }> }>;
  }>('/v1/settings/backoffice/smsenvoi/subaccounts'),
  /** Le droit d'envoyer d'un magasin — TOUJOURS les trois canaux, posés ou non. */
  boChannelAllowances: (gln: string) => req<{ ok: boolean; error?: string; allowances?: BoChannelAllowance[] }>(
    `/v1/settings/backoffice/messaging/allowance/${encodeURIComponent(gln)}`),
  /** Suspend (`allowance: 0`, motif obligatoire) ou rend (`null`) le droit d'envoyer sur un canal. */
  setBoChannelAllowance: (gln: string, v: { channel: string; allowance: number | null; reason?: string | null }) =>
    req<{ ok: boolean; error?: string; allowance?: BoChannelAllowance }>(
      `/v1/settings/backoffice/messaging/allowance/${encodeURIComponent(gln)}`,
      { method: 'PUT', body: JSON.stringify(v) }),
  /** Nos comptes enregistrés — n'appelle PAS l'opérateur, donc lisible même s'il est en panne. */
  boMessagingAccounts: () => req<{ ok: boolean; error?: string; accounts?: BoMessagingAccount[] }>(
    '/v1/settings/backoffice/messaging/accounts'),
  /** Crée un sous-compte (et le rattache si `gln` est fourni). Le mot de passe est rendu UNE fois. */
  createBoSubaccount: (v: { login: string; label?: string; email?: string; companyName?: string; gln?: string }) =>
    req<{ ok: boolean; error?: string; accountId?: string; externalId?: string; login?: string; password?: string;
      attached?: boolean; attachError?: string;
      orphan?: { externalId: string; login: string; password: string; reason: string } }>(
      '/v1/settings/backoffice/smsenvoi/subaccounts', { method: 'POST', body: JSON.stringify(v) }),
  /** Rattache un magasin — la réponse porte l'ÉPREUVE, seul témoin que le canal existe vraiment. */
  attachBoMessagingAccount: (v: { gln: string; accountId?: string; externalId?: string; login?: string; label?: string; password?: string }) =>
    req<{ ok: boolean; error?: string; accountId?: string; probe?: BoStoreAccountProbe }>(
      '/v1/settings/backoffice/messaging/attach', { method: 'POST', body: JSON.stringify(v) }),
  /** Détache un magasin. ⚠️ Le compte reste chez l'opérateur : détacher n'est pas supprimer. */
  detachBoMessagingAccount: (gln: string) =>
    req<{ ok: boolean; error?: string; detached?: boolean }>(
      `/v1/settings/backoffice/messaging/attach/${encodeURIComponent(gln)}`, { method: 'DELETE' }),
  /** « Ce magasin peut-il envoyer ? » — en lecture seule, aucun SMS n'est envoyé pour tester. */
  testBoStoreAccount: (gln: string) =>
    req<{ ok: boolean; error?: string; probe?: BoStoreAccountProbe }>(
      `/v1/settings/backoffice/messaging/test/${encodeURIComponent(gln)}`, { method: 'POST' }),
  updateBoSetting: (v: { key: string; value?: string | null; secret?: string }) =>
    req<{ settings: BoPlatformSetting[] }>('/v1/settings/backoffice/setting', { method: 'PUT', body: JSON.stringify(v) }),
  updateBoMail: (v: Partial<MailSettings> & { secret?: string }) =>
    req<{ mail: MailSettings }>('/v1/settings/backoffice/mail', { method: 'PUT', body: JSON.stringify(v) }),
  // Transfert wallet : supervision, relance des lettres mortes, pose du jeton d un magasin.
  boWallet: () => req<WalletState>('/v1/settings/backoffice/wallet'),
  retryBoWallet: (gln: string) =>
    req<{ retried: number }>('/v1/settings/backoffice/wallet/retry', { method: 'POST', body: JSON.stringify({ gln }) }),
  setBoWalletToken: (glns: string[], token: string) =>
    req<{ detail: string; byStore: { gln: string; ok: boolean; error?: string }[] }>(
      '/v1/settings/backoffice/wallet/token', { method: 'PUT', body: JSON.stringify({ glns, token }) }),
  testBoMail: (to: string) => req<{ ok: boolean; error?: string; effective: string }>('/v1/settings/backoffice/mail/test', { method: 'POST', body: JSON.stringify({ to }) }),
  // Invitations de depot (formulaire externe, SA-2.12). L issue de l e-mail est REELLE (jamais presumee) ;
  // l URL revient toujours — copiable a la main si l envoi a echoue.
  createDraftInvite: (body: { draftType: 'client' | 'magasin'; email: string; tenantId?: string }) =>
    req<DraftInviteResult>('/v1/draft-invites', { method: 'POST', body: JSON.stringify(body) }),
  requestDraftChanges: (id: string, reason: string) =>
    req<DraftInviteResult>(`/v1/annuaire/drafts/${id}/request-changes`, { method: 'POST', body: JSON.stringify({ reason }) }),
  // Propagation des referentiels (SA-10.2) : mesurer l ecart par magasin, puis appliquer l arbitrage.
  analyzePropagation: (lists?: string[]) =>
    req<{ stores: PropagationStoreDiff[] }>('/v1/referentials/propagation/analyze', { method: 'POST', body: JSON.stringify(lists?.length ? { lists } : {}) }),
  applyPropagation: (stores: { id: string; impose: string[] }[], lists?: string[]) =>
    req<{ results: PropagationApplyResult[] }>('/v1/referentials/propagation/apply', { method: 'POST', body: JSON.stringify({ stores, ...(lists?.length ? { lists } : {}) }) }),
  updateLinkSettings: (v: { pairingTtlHours?: number; draftInviteTtlHours?: number }) =>
    req<{ ok: true }>('/v1/settings/links', { method: 'PATCH', body: JSON.stringify(v) }),
  referentialTrades: () => req<{ trades: TradeTypeRow[] }>('/v1/referentials/trades'),
  createTrade: (v: { code: string; label: string; description?: string; lists?: string[] }) =>
    req<{ id: string }>('/v1/referentials/trades', { method: 'POST', body: JSON.stringify(v) }),
  updateTrade: (id: string, patch: { label?: string; description?: string; lists?: string[] }) =>
    req<{ ok: true }>(`/v1/referentials/trades/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteTrade: (id: string) => req<{ ok: true }>(`/v1/referentials/trades/${id}`, { method: 'DELETE' }),
  referentialDefinitions: () => req<{ definitions: RefListDef[]; socle: string[] }>('/v1/referentials/definitions'),
  createRefList: (v: { key: string; label: string; addLabel?: string; note?: string; category?: string; cols?: RefColDef[]; storeKind?: string | null }) =>
    req<{ key: string }>('/v1/referentials/definitions', { method: 'POST', body: JSON.stringify(v) }),
  updateRefList: (key: string, patch: { label?: string; addLabel?: string; note?: string; category?: string; cols?: RefColDef[] }) =>
    req<{ ok: true }>(`/v1/referentials/definitions/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteRefList: (key: string) => req<{ ok: true }>(`/v1/referentials/definitions/${encodeURIComponent(key)}`, { method: 'DELETE' }),
  referentials: () => req<{ referentials: ReferentialRow[] }>('/v1/referentials'),
  createReferential: (v: { list: string; code: string; label: string; ord?: number; attrs?: Record<string, unknown> }) =>
    req<{ id: string }>('/v1/referentials', { method: 'POST', body: JSON.stringify(v) }),
  updateReferential: (id: string, patch: { label?: string; ord?: number; attrs?: Record<string, unknown> }) =>
    req<{ ok: true }>(`/v1/referentials/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteReferential: (id: string) => req<{ ok: true }>(`/v1/referentials/${id}`, { method: 'DELETE' }),
  // Vocabulaire — champs, dictionnaire des termes, terme retenu par métier, propagation aux magasins.
  vocabulary: () => req<VocabularyState>('/v1/vocabulary'),
  // ⚠️ Sans `key` : le serveur DÉRIVE la clé du mot français et la rend — l'écran ne la tape jamais, il
  //    ne la connaît qu'à la réponse. `key` reste acceptée pour qui l'impose (reprise, script), pas l'écran.
  createVocabularyTerm: (v: { key?: string; texts: VocabularyTexts }) =>
    req<{ key: string }>('/v1/vocabulary/terms', { method: 'POST', body: JSON.stringify(v) }),
  updateVocabularyTerm: (key: string, texts: Partial<VocabularyTexts>) =>
    req<{ ok: true }>(`/v1/vocabulary/terms/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify({ texts }) }),
  deleteVocabularyTerm: (key: string) =>
    req<{ ok: true }>(`/v1/vocabulary/terms/${encodeURIComponent(key)}`, { method: 'DELETE' }),
  // ⚠️ PUT = la carte COMPLÈTE des dérogations du métier : un champ absent (ou null) retombe sur le terme
  //    de plateforme. N'envoyer que le champ modifié effacerait les autres dérogations en silence.
  setTradeVocabulary: (code: string, fields: Record<string, string | null>) =>
    req<{ ok: true }>(`/v1/vocabulary/trades/${encodeURIComponent(code)}`, { method: 'PUT', body: JSON.stringify({ fields }) }),
  analyzeVocabularyPropagation: () =>
    req<{ stores: VocabularyPropagationStore[] }>('/v1/vocabulary/propagation/analyze', { method: 'POST', body: JSON.stringify({}) }),
  applyVocabularyPropagation: (stores: string[]) =>
    req<{ results: VocabularyPropagationResult[] }>('/v1/vocabulary/propagation/apply', { method: 'POST', body: JSON.stringify({ stores }) }),
  // ── Gabarits d'e-mail de l'ÉDITEUR — les courriers qu'Orgazone envoie en son propre nom ──────
  // ⚠️ NI CRÉATION NI NOM : un usage a UN gabarit, et son absence vaut le courrier du logiciel.
  //    D'où un PUT par usage plutôt qu'un POST, et un DELETE qui RESTAURE au lieu de retirer.
  consoleEmailTemplates: () => req<{ templates: ConsoleEmailTemplate[] }>('/v1/email-templates'),
  saveConsoleEmailTemplate: (purpose: string, tpl: EmailTemplateInput) =>
    req<{ template: ConsoleEmailTemplate }>(`/v1/email-templates/${encodeURIComponent(purpose)}`, { method: 'PUT', body: JSON.stringify(tpl) }),
  /** ⚠️ `restored: false` = il n'y avait DÉJÀ plus de ligne (autre onglet, double clic) : l'écran le dit. */
  resetConsoleEmailTemplate: (purpose: string) =>
    req<{ restored: boolean }>(`/v1/email-templates/${encodeURIComponent(purpose)}`, { method: 'DELETE' }),
  /** Le référentiel des COMMUNES — lecture seule : ce que chaque magasin porte, et ce qui lui manque. */
  analyzeCommunes: () =>
    req<CommunesAnalysis>('/v1/referentials/communes/analyze', { method: 'POST', body: JSON.stringify({}) }),
  /** ⚠️ L'écart est RELU au moment d'écrire : entre l'analyse et le clic, une propagation a pu passer. */
  applyCommunes: (stores: string[]) =>
    req<{ results: CommunesApplyResult[] }>('/v1/referentials/communes/apply', { method: 'POST', body: JSON.stringify({ stores }) }),
  /** Compteurs fiscaux RÉELS (fiscal_sequence de la base magasin) — provisioned=false : pas de base. */
  /** ⚠️ `clients: null` = « je n'ai pas pu compter » (table absente), jamais « zéro client ». */
  /**
   * Ce que la fiche lit de la base du magasin, en une connexion : séquences, effectif clients, ventes et taille.
   * ⚠️ `sales` est le compte qui garde la réinitialisation : `null` = pas su le compter (`salesError`), JAMAIS zéro.
   */
  storeCounters: (id: string) => req<{ provisioned: boolean; rows: { register_id: string; kind: string; last_seq: number }[]; clients?: number | null; clientSeq?: { register_id: string; last_seq: number }[]; sales?: number | null; salesError?: string | null; dbSizeBytes?: number | null }>(`/v1/annuaire/stores/${id}/counters`),
  storeSettings: (id: string) => req<{ settings: Record<string, string> }>(`/v1/annuaire/stores/${id}/settings`),
  // `descend` = issue REELLE de la descente aux caisses (journalisation du canal settings au BO) —
  // le toast la restitue telle quelle, jamais un « ça descendra » presume.
  updateStoreSettings: (id: string, settings: Record<string, string>) =>
    req<{ ok: true; descend: { ok: boolean; note: string } }>(`/v1/annuaire/stores/${id}/settings`, { method: 'PATCH', body: JSON.stringify({ settings }) }),
  // GROUPES DE MISE À JOUR (§32.6ter). `error` non nul = le store n'a pas pu être lu : la liste est vide et
  // le sélecteur ne propose que le flux public, en disant pourquoi.
  storeAppGroups: (slug: string) => req<{ groups: StoreGroup[]; error: string | null }>(`/v1/store-apps/${encodeURIComponent(slug)}/groups`),
  // Nos applications et ce que le store en dit — un store illisible rend son motif, jamais une liste vide.
  storeApps: () => req<StoreAppsSurvey>('/v1/store-apps'),
  storeUpdateGroups: (id: string) => req<{ choices: UpdateGroupChoice[] }>(`/v1/annuaire/stores/${id}/update-groups`),
  // `descent` = ce qui est arrivé côté caisses, tel quel — jamais un « ça descendra » présumé.
  setStoreUpdateGroup: (id: string, app: string, enrollToken: string | null) =>
    req<{ ok: true; choice: UpdateGroupChoice | null; descent: { ok: boolean; note: string } }>(`/v1/annuaire/stores/${id}/update-groups/${encodeURIComponent(app)}`, { method: 'PUT', body: JSON.stringify({ enrollToken }) }),
  // (Re)déclarer un magasin au back-office (provisioning unifié) ; rotate=true régénère la clé API.
  // `syncMode` = le sens réécrit dans la base du magasin et sa descente aux caisses, tels qu'ils se sont passés.
  declareStoreBo: (id: string, rotate = false) =>
    req<{ ok: true; syncMode?: { written: boolean; ok: boolean; note: string } }>(`/v1/annuaire/stores/${id}/bo-declaration`, { method: 'POST', body: JSON.stringify({ rotate }) }),
  // Le SENS de synchronisation (§12.9.1) — super-admin seul, motif obligatoire. `descend` = issue réelle côté caisses.
  setStoreSyncMode: (id: string, mode: 'mono' | 'bi', reason: string) =>
    req<{ ok: true; mode: 'mono' | 'bi'; descend: { ok: boolean; note: string } }>(`/v1/annuaire/stores/${id}/sync-mode`, { method: 'PUT', body: JSON.stringify({ mode, reason }) }),
  // Licences par outil (SA-1.6) — une licence se FERME (historique du contrat), elle ne se supprime pas.
  openLicense: (tenantId: string, product: string) =>
    req<{ ok: true }>(`/v1/annuaire/tenants/${tenantId}/licenses`, { method: 'POST', body: JSON.stringify({ product }) }),
  closeLicense: (tenantId: string, licenseId: string) =>
    req<{ ok: true }>(`/v1/annuaire/tenants/${tenantId}/licenses/${licenseId}`, { method: 'DELETE' }),
  // ACCÈS CROSS-TENANT (doctrine d'identité §4ter) : demander au back-office un TICKET d'entrée pour le
  // compte Orgazone connecté, sur CE magasin. Usage unique, quelques minutes de validité.
  // ⚠️ Le ticket ne se journalise ni ne se stocke : il ne fait que traverser le navigateur (cf.
  // `./cross-tenant.ts`, qui le poste en corps de formulaire — jamais dans une URL).
  // ⚠️ Le MOTIF est exigé par le serveur : il entre au journal de la Console (l'historique du magasin) et, porté par le
  //    ticket, au journal d'accès du client côté back-office.
  openStoreBackoffice: (id: string, reason: string) =>
    req<CrossTenantEntry>(`/v1/annuaire/stores/${id}/cross-tenant`, { method: 'POST', body: JSON.stringify({ reason }) }),
  // Déclarer le magasin à l'EDI (feed d'identité de site : GLN + nom). Refus explicite si l'EDI n'est
  // pas configuré côté serveur (409) — l'écran le dit au lieu de laisser croire à un succès.
  declareStoreEdi: (id: string) =>
    req<{ ok: true; ediDeclaredAt: string }>(`/v1/annuaire/stores/${id}/edi-declaration`, { method: 'POST' }),
  // Identités & accès — comptes staff (création réservée super-admin côté serveur).
  // ⚠️ inviteSent=false = compte créé mais l'IdP n'a pas (encore) envoyé l'e-mail (transport à configurer) → « Renvoyer ».
  staff: () => req<{ staff: StaffRow[] }>('/v1/staff'),
  createStaff: (body: { firstName: string; lastName: string; email: string; role: string }) =>
    req<{ staff: { id: string; email: string }; inviteSent: boolean; alreadyActive: boolean }>('/v1/staff', { method: 'POST', body: JSON.stringify(body) }),
  resendInvite: (id: string) =>
    req<{ inviteSent: boolean; alreadyActive: boolean }>(`/v1/staff/${id}/resend`, { method: 'POST' }),
  updateStaff: (id: string, patch: { role?: string; status?: 'active' | 'disabled' }) =>
    req<{ ok: true }>(`/v1/staff/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteStaff: (id: string) => req<{ ok: true }>(`/v1/staff/${id}`, { method: 'DELETE' }),
  // Lectures IdP (journal, applications connectées, fiche compte) — remplissent les blocs « à venir ».
  identityAudit: (email?: string) => req<{ events: IdpAuditEvent[] }>(`/v1/identity/audit${email ? `?email=${encodeURIComponent(email)}` : ''}`),
  /** Les applications de l'IdP et son origine publique (`null` sans fédération). */
  identityApps: () => req<{ apps: RpApp[]; issuer: string | null }>('/v1/identity/apps'),
  // TOUTES les identités de l'IdP (staff ET clients) — vue d'ensemble, 2FA incluse.
  identityAccounts: (realm?: string, q?: string) =>
    req<{ accounts: IdpAccountRow[] }>(`/v1/identity/accounts?${new URLSearchParams({ ...(realm ? { realm } : {}), ...(q ? { q } : {}) }).toString()}`),
  identityAccount: (email: string) => req<IdpAccountInfo>(`/v1/identity/account?email=${encodeURIComponent(email)}`),
  // Recherche transverse du rail du haut (clients, magasins, appareils, comptes).
  search: (q: string) => req<SearchResults>(`/v1/search?q=${encodeURIComponent(q)}`),
  // Grants RBAC : lire, accorder (scope + expiration optionnelle), révoquer.
  identityGrants: (email: string) => req<{ grants: IdpGrant[] }>(`/v1/identity/grants?email=${encodeURIComponent(email)}`),
  grantScope: (body: { email: string; scope: string; expiresAt?: string }) => req<{ ok: true }>('/v1/identity/grants', { method: 'POST', body: JSON.stringify(body) }),
  revokeGrant: (id: string) => req<{ ok: true }>(`/v1/identity/grants/${id}`, { method: 'DELETE' }),
  // Explorateur de bases (lecture seule, gaté console.read) — bases explorables, schéma d'une base, échantillon d'une table.
  explorerBases: () => req<{ bases: ExplorerBase[] }>('/v1/explorer/bases'),
  explorerSchema: (base: string) => req<ExplorerSchema>(`/v1/explorer/schema?base=${encodeURIComponent(base)}`),
  explorerRelations: (base: string) => req<{ relations: ExplorerRelation[] }>(`/v1/explorer/relations?base=${encodeURIComponent(base)}`),
  explorerFields: (base: string) => req<{ fields: ExplorerField[] }>(`/v1/explorer/fields?base=${encodeURIComponent(base)}`),
  explorerRows: (base: string, table: string, query: ExplorerQuery = {}) => {
    const params = new URLSearchParams({ base, table, limit: String(query.limit ?? 50), offset: String(query.offset ?? 0) });
    if (query.q) params.set('q', query.q);
    if (query.sort) params.set('sort', query.sort);
    if (query.direction) params.set('direction', query.direction);
    if (query.filters?.length) params.set('filters', JSON.stringify(query.filters));
    return req<ExplorerRows>(`/v1/explorer/rows?${params}`);
  },
  // Correction de masse (SA-7.2, gaté console.explorer.write = super-admin) : appliquer, lister l'historique, annuler.
  // ⚠️ `journalError` : l'écriture a EU LIEU mais le journal d'audit a échoué (rollback indisponible) — à afficher.
  explorerCorrect: (body: { base: string; table: string; column: string; op: 'set' | 'prefix' | 'mul'; value: string; motif: string; pks: string[] }) =>
    req<{ id: string | null; affected: number; journalError?: string }>('/v1/explorer/correct', { method: 'POST', body: JSON.stringify(body) }),
  // Suppression de lignes (SA-7.3, super-admin) : cible = PK sélectionnées, lignes complètes conservées → rollback = réinsertion.
  explorerDelete: (body: { base: string; table: string; motif: string; pks: string[] }) =>
    req<{ id: string | null; deleted: number; journalError?: string }>('/v1/explorer/delete', { method: 'POST', body: JSON.stringify(body) }),
  explorerCorrections: (base: string) => req<{ corrections: ExplorerCorrection[] }>(`/v1/explorer/corrections?base=${encodeURIComponent(base)}`),
  explorerRollback: (id: string) => req<{ ok: true; restored: number }>(`/v1/explorer/corrections/${id}/rollback`, { method: 'POST' }),
  // Kill-switch (SA-4) : liste (la règle auto « location » s'évalue à la lecture), pose (délégation par
  // domaine côté serveur), levée (même délégation — vaut aussi pour un blocage auto).
  // Journal d'activité (dashboard) : agrégation en lecture des journaux réels de l'annuaire.
  // `total` accompagne toujours les lignes : la page Journal doit pouvoir DIRE qu'elle n'en montre
  // qu'une tête, sinon un plafond silencieux se lit comme l'intégralité du journal.
  /** Historique d'un client : le journal Console filtre sur lui ET ses magasins, cote SERVEUR.
   *  `since` = date depuis laquelle les evenements portent leur rattachement (les anterieurs n'en
   *  ont pas, et la table etant append-only ils n'en auront jamais). */
  tenantActivity: (id: string) => req<{ events: ActivityEvent[]; since: string }>(`/v1/annuaire/tenants/${id}/activity`),
  /** Historique d'un magasin : les opérations de la Console qui le concernent (même `since` que celui du client). */
  storeActivity: (id: string) => req<{ events: ActivityEvent[]; since: string }>(`/v1/annuaire/stores/${encodeURIComponent(id)}/activity`),
  /** Sante technique du parc (SA-9.1) — jamais de chiffre d affaires. */
  /** La santé du parc — une tournée partagée quelques minutes au serveur (`measuredAt`) ; `fresh` en force une neuve. */
  supervision: (fresh = false) => req<{ stores: SupervisionStore[]; publishedVersion: string | null; publishedError: string | null; measuredAt: string }>(`/v1/supervision${fresh ? '?fresh=1' : ''}`),
  /**
   * L'INVENTAIRE DES ORPHELINS — ce qui survit à un magasin que l'annuaire ne connaît plus.
   * ⚠️ LECTURE SEULE : il NOMME, il ne nettoie pas. Le retrait passe par la suppression du magasin.
   */
  orphelins: () => req<RapportOrphelins>(`/v1/annuaire/orphelins`),
  /** Le journal de la Console — `since` borne la lecture ET le total : la période se filtre au serveur. */
  activity: (limit?: number, since?: string) => {
    const q = new URLSearchParams();
    if (limit) q.set('limit', String(limit));
    if (since) q.set('since', since);
    const s = q.toString();
    return req<{ events: ActivityEvent[]; total: number; limit: number }>(`/v1/activity${s ? `?${s}` : ''}`);
  },
  blocks: () => req<{ active: BlockRow[]; history: BlockRow[] }>('/v1/blocks'),
  // VEILLE DES DÉFAILLANCES (SA-9.3) — la veille propose, l'humain décide : `propagated` ne vaut que pour l'avertissement.
  legalWatch: () => req<LegalWatchView>('/v1/legal-watch'),
  tenantLegalEvents: (tenantId: string) => req<TenantLegalView>(`/v1/annuaire/tenants/${tenantId}/legal-events`),
  decideLegal: (id: string, decision: LegalDecision, reason: string) =>
    req<{ ok: true; decision: LegalDecision; propagated: boolean | null; failures: unknown[] }>(`/v1/legal-watch/${id}/decision`, { method: 'POST', body: JSON.stringify({ decision, reason }) }),
  runLegalWatch: () => req<{ watched: number; unwatched: number; newEvents: number; errors: { siren: string; source: string; error: string }[] }>('/v1/legal-watch/run', { method: 'POST', body: '{}' }),
  // Rôles et droits — le catalogue des modules vient du SERVEUR : le recopier ici ferait diverger
  // l'écran et la validation, et proposerait une case que le serveur refuse.
  roles: () => req<{ total: number; roles: RoleRow[] }>('/v1/access/roles'),
  role: (code: string) => req<RoleDetail>(`/v1/access/roles/${encodeURIComponent(code)}`),
  saveRole: (code: string, body: { rights: Record<string, RightLevel>; scope: 'followers' | 'template'; reason: string }) =>
    req<{ changed: number; pinned: number; followers: number }>(`/v1/access/roles/${encodeURIComponent(code)}`, { method: 'PUT', body: JSON.stringify(body) }),
  createRole: (body: { from: string; code: string; name: string }) =>
    req<{ code: string }>('/v1/access/roles', { method: 'POST', body: JSON.stringify(body) }),
  deleteRole: (code: string) => req<{ ok: true }>(`/v1/access/roles/${encodeURIComponent(code)}`, { method: 'DELETE' }),
  realignTenant: (code: string, tenantId: string) =>
    req<{ ok: true }>(`/v1/access/roles/${encodeURIComponent(code)}/tenants/${encodeURIComponent(tenantId)}`, { method: 'DELETE' }),
  // Comptes CLIENTS d'un client — l'identité vit chez l'IdP, le rattachement et le périmètre ici.
  // ⚠️ Tout passe par l'id du client : un accès ne peut pas sortir du périmètre de la fiche ouverte.
  tenantAccounts: (tenantId: string) => req<{ accounts: TenantAccount[] }>(`/v1/annuaire/tenants/${tenantId}/accounts`),
  // Ce qui est ATTRIBUABLE chez ce client (outils sous licence, magasins, rôles) — décidé par le serveur.
  tenantAccessOptions: (tenantId: string) => req<TenantAccessOptions>(`/v1/annuaire/tenants/${tenantId}/access-options`),
  inviteTenantAccount: (tenantId: string, body: { email: string; firstName: string; lastName: string }) =>
    req<{ ok: true; created?: boolean; alreadyActive?: boolean; inviteSent?: boolean }>(`/v1/annuaire/tenants/${tenantId}/accounts`, { method: 'POST', body: JSON.stringify(body) }),
  detachTenantAccount: (tenantId: string, email: string) =>
    req<{ ok: true; revoked: number }>(`/v1/annuaire/tenants/${tenantId}/accounts/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  // Ouvre le back-office des magasins cochés à un compte du client — c'est le back-office qui applique l'accès.
  openBoAccess: (tenantId: string, email: string, body: { role: 'admin' | 'operator'; glns: string[] }) =>
    req<{ ok: true; created: boolean; addedGlns: string[] }>(`/v1/annuaire/tenants/${tenantId}/accounts/${encodeURIComponent(email)}/bo-access`, { method: 'POST', body: JSON.stringify(body) }),
  grantTenantAccess: (tenantId: string, email: string, body: { tool: string; role: string; gln?: string }) =>
    req<{ ok: true; scope: string }>(`/v1/annuaire/tenants/${tenantId}/accounts/${encodeURIComponent(email)}/grants`, { method: 'POST', body: JSON.stringify(body) }),
  revokeTenantAccess: (tenantId: string, email: string, grantId: string) =>
    req<{ ok: true }>(`/v1/annuaire/tenants/${tenantId}/accounts/${encodeURIComponent(email)}/grants/${encodeURIComponent(grantId)}`, { method: 'DELETE' }),
  // Compte d'administration des caisses : lire l'état (et la descente), poser ou faire tourner le secret.
  adminAccount: () => req<AdminAccountState>('/v1/admin-account'),
  setAdminAccount: (body: { password: string; label?: string }) =>
    req<AdminAccountResult>('/v1/admin-account', { method: 'POST', body: JSON.stringify(body) }),
  setAdminMobilePin: (body: { pin: string }) =>
    req<AdminAccountResult>('/v1/admin-account/mobile-pin', { method: 'POST', body: JSON.stringify(body) }),
  /**
   * RENVOI du compte COURANT aux magasins qui ne l'ont pas reçu — un geste, pas un effet de bord.
   * ⚠️ Il a remplacé une reprise cachée dans la LECTURE de l'état : ouvrir cette page distribuait le
   *    compte d'administration à toute la flotte. Ne change ni le mot de passe ni la rotation.
   */
  pushAdminAccount: () => req<AdminAccountResult>('/v1/admin-account/push', { method: 'POST' }),
  createBlock: (body: { scope: 'subscription' | 'store' | 'device'; targetId: string; level: 'warn' | 'lock'; reason: string }) =>
    req<{ id: string | null } & BlockPropagation>('/v1/blocks', { method: 'POST', body: JSON.stringify(body) }),
  liftBlock: (id: string, reason?: string) =>
    req<{ ok: true } & BlockPropagation>(`/v1/blocks/${id}/lift`, { method: 'POST', body: JSON.stringify({ reason }) }),
  // Migrations multi-base (SA-6) — état RÉEL de chaque base magasin vs la cible du schéma, puis application
  // (deploy_ops/super-admin). storeIds absent = toutes les bases actives ; le serveur ne migre que le manquant.
  migrationsStatus: () => req<MigrationsStatus>('/v1/migrations/status'),
  // Pré-vol : bilan de santé RÉEL des bases ciblées (lecture pure) — affiché dans la modale avant Migrer.
  migrationsPreflight: (storeIds?: string[]) =>
    req<{ results: StorePreflight[] }>('/v1/migrations/preflight', { method: 'POST', body: JSON.stringify(storeIds ? { storeIds } : {}) }),
  migrationsRun: (storeIds?: string[], snapshot?: boolean) =>
    req<MigrationsRunReport>('/v1/migrations/run', { method: 'POST', body: JSON.stringify({ ...(storeIds ? { storeIds } : {}), ...(snapshot ? { snapshot: true } : {}) }) }),
  // Instantanés (copie intégrale pré-migration) : liste, restauration (DESTRUCTIF), suppression.
  migrationsSnapshots: () => req<{ snapshots: SnapshotRow[] }>('/v1/migrations/snapshots'),
  migrationsRestoreSnapshot: (name: string) =>
    req<{ ok: true; db: string; restoredTo: number }>('/v1/migrations/snapshots/restore', { method: 'POST', body: JSON.stringify({ name }) }),
  migrationsDropSnapshot: (name: string) =>
    req<{ ok: true }>('/v1/migrations/snapshots/drop', { method: 'POST', body: JSON.stringify({ name }) }),
  /**
   * ÉLAGUER LE JOURNAL des magasins désignés — ce que toutes leurs caisses actives ont déjà lu.
   * ⚠️ LES DEUX DURÉES VOYAGENT TOUJOURS : le serveur refuse une valeur invalide plutôt que de la
   * rabattre sur un défaut, pour que le nombre affiché soit exactement celui qui agit (§23.0).
   */
  migrationsPurgeJournal: (glns: string[], retentionDays: number, forgetAfterDays: number) =>
    req<PurgeJournalReport>('/v1/migrations/purge-journal', {
      method: 'POST', body: JSON.stringify({ glns, retentionDays, forgetAfterDays }),
    }),
  // Supervision — santé des services (lecture) + gestion des services surveillés (super-admin).
  healthServices: () => req<{ services: ServiceHealth[]; measure: HealthMeasure }>('/v1/health/services'),
  /**
   * L'espace disque de la flotte — le DERNIER relèvement rangé, pas une mesure à la demande.
   * ⚠️ `at` accompagne chaque point : à deux relèvements par jour, un chiffre sans sa date se lirait
   *    comme s'il était de maintenant. `points: 0` = JAMAIS relèvé, ce qui n'est pas « tout va bien ».
   */
  healthDisk: () => req<DiskState>('/v1/health/disk'),
  backups: () => req<BackupState>('/v1/backups'),
  backupHistory: (q: { database?: string; limit?: number } = {}) =>
    req<BackupHistory>('/v1/backups/history?' + new URLSearchParams(
      Object.entries(q).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]),
    ).toString()),
  setBackupConfig: (c: BackupConfigState) =>
    req<{ config: BackupConfigState; note: string }>('/v1/backups/config', { method: 'PUT', body: JSON.stringify(c) }),
  healthHistory: (service: string, hours: number) => req<History>(`/v1/health/history?service=${encodeURIComponent(service)}&hours=${hours}`),
  settingsServices: () => req<{ services: MonitoredService[] }>('/v1/settings/services'),
  createService: (body: { name: string; url: string }) =>
    req<{ service: { id: string } }>('/v1/settings/services', { method: 'POST', body: JSON.stringify(body) }),
  updateService: (id: string, patch: { name?: string; url?: string; enabled?: boolean }) =>
    req<{ ok: true }>(`/v1/settings/services/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteService: (id: string) => req<{ ok: true }>(`/v1/settings/services/${id}`, { method: 'DELETE' }),
};
